// Carrom page — UI scaffold (Slice 1 of CARROM_BUILD_PLAN.md).
//
// All gameplay control lives in <Board>: tap-baseline to reposition
// striker, drag-back-and-release to shoot. There are no Shoot or
// Reset buttons; the slingshot gesture and lifting the finger are
// the only inputs. <Board> handles the planck.js shot simulation
// and animates the timeline locally; this page reacts to its
// `onShotResult` callback to update score / fouls / turn state.
//
// Server-authoritative networking, voice chat, recovery, wallet sync,
// concurrent-match guard, real bots, etc. all land in later slices.
// For now the matchmaking flow is mocked with setTimeout so the
// visuals + UX can be reviewed without a backend.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useWallet } from '@baaligames/shared-ui/lib/wallet';
import { api } from '@baaligames/shared-ui/lib/api';
import { isSessionAcked, markSessionAcked, clearSessionAcked } from '@baaligames/shared-ui/lib/recoveryAck';
import Icon from '@baaligames/shared-ui/components/Icon';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import InsufficientFundsModal from '@baaligames/shared-ui/components/InsufficientFundsModal';
import ConcurrentMatchModal from '@baaligames/shared-ui/components/ConcurrentMatchModal';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader, flattenImageList } from '@baaligames/shared-ui/lib/useAssetPreloader';
import { useToast } from '@baaligames/shared-ui/lib/toast';

import Board, {
  initialLayout,
  STRIKER_HOME,
  BASELINE_Y,
  canPlaceStrikerAt,
  type Coin,
  type Striker,
  type Seat,
} from './Board';
import type { ShotResult } from './physics';
import {
  playFoul,
  playCollision,
  playPocketDrop,
  playTap,
  playWinFanfare,
  playLossTone,
  playYourTurnChime,
  playYourTurnAgainChime,
  primeAudio,
  installAudioUnlock,
  setSoundEnabled as setGameSoundEnabled,
  startUrgencyBeep,
  stopUrgencyBeep,
} from './sounds';
import { CarromWS, type WSStatus } from './ws';
import { WSVoice } from './wsVoice';

// Server seats are integers (1..4); the Board UI uses string keys
// ('p1'..'p4'). Translate at the WS boundary so the rest of the
// page can keep using the existing Seat type unchanged.
const seatNumToKey = (n: number): Seat => (`p${n}` as Seat);
const seatKeyToNum = (k: Seat): number => Number(k.slice(1));

// Shared shell from Ludo (app-container, safe-area handling).
import '../ludo/ludo.css';
import './carrom.css';

// Image set — placeholders cloned from Ludo into
// `backend/public/games/carrom/*` so the UI is fully visible before
// final art lands. Drop a real PNG over any of these and refresh.
const IMG = {
  logo:     '/games/carrom/Logo.png',
  coin:     '/games/carrom/Coin.png',
  cash:     ['/games/carrom/Cash-1.png', '/games/carrom/Cash-2.png', '/games/carrom/Cash-3.png', '/games/carrom/Cash-4.png'],
  // pawn2/pawn4 removed — Carrom MVP is 2P-only, no mode chooser.
  // Image files remain on disk for the future 4P mode (deferred).
  trophy:   '/games/carrom/Victory_Trophy.png',
  defeat:   '/games/carrom/Defeat_Medal.png',
  quitWarn: '/games/carrom/Quit_Warning.png',
  player1:  '/games/carrom/RC_Player_blue.png',
  player2:  '/games/carrom/RC_Player_red.png',
  player3:  '/games/carrom/RC_Player_yellow.png',
  player4:  '/games/carrom/RC_Player_green.png',
  settings: '/games/carrom/Settings.png',
  plus:     '/games/carrom/Plus.png',
};

// Stable preload list — module-scoped so identity is fixed across
// renders. Includes the heavy /games/carrom/Board.png even though
// the lobby doesn't render it directly: by the time the player taps
// a cash tile and enters the VS / playing phase, the board image
// needs to be ready or the felt flashes blank for several hundred
// ms on first match. Preloading at lobby entry hides that latency.
const CARROM_PRELOAD_URLS = [
  ...flattenImageList(IMG),
  '/games/carrom/Board.png',
];

type Phase = 'lobby' | 'queue' | 'vs' | 'playing' | 'result';

const BET_TIERS_FALLBACK = [10, 50, 100, 250];
const PLATFORM_FEE_FALLBACK = 0; // platform fee ABOLISHED (H5/H7)
// Mirror the server defaults from CarromGame as boot-time fallbacks.
// The page fetches /api/carrom/config on mount and overrides these
// with the admin-set live values (so admin edits propagate without
// a redeploy). Order of precedence:
//   1. admin DB value (carrom_config table)
//   2. server file default in CarromGame
//   3. these constants (used until /carrom/config resolves)
const TURN_SECONDS_FALLBACK              = 30;
const URGENCY_BEEP_LEAD_SEC_FALLBACK     = 6;
const VS_INTRO_SECONDS_FALLBACK          = 3;
const MATCH_END_RESULT_DELAY_SEC_FALLBACK = 2.5;

const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function Carrom() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { balance: wallet } = useWallet();
  const toast = useToast();

  // Bootstrap gate — see matching block in Ludo / XO / Chess. The
  // loading overlay stays up until BOTH the WS auth has landed AND
  // every preloaded asset URL (including the heavy Board.png) has
  // settled. Without the board preloaded, the first match would
  // flash a blank felt for hundreds of ms while the PNG decoded.
  const assetStatus = useAssetPreloader(CARROM_PRELOAD_URLS, 600);
  const [authReady, setAuthReady] = useState(false);

  const [phase, setPhase] = useState<Phase>('lobby');
  // Player count is fixed at 2 for the MVP — 4-player is deferred,
  // see CARROM_BUILD_PLAN.md §13. Kept as a constant so if/when 4P
  // ships the only place to change is one line + the engine paths.
  const PLAYER_COUNT = 2 as const;
  const [selectedBet, setSelectedBet] = useState<number>(BET_TIERS_FALLBACK[0]);
  // Authoritative match pot. Hydrated from `msg.pot` on
  // carrom_match_start / carrom_reconnect_sync — the server is the
  // source of truth here. Pre-fix the top bar derived the pot from
  // `selectedBet * PLAYER_COUNT`, which went stale whenever the
  // user's lobby preference drifted from the server-locked bet
  // (mid-match refresh, recovery flow, tier-list change after a
  // match started). XO/Ludo/Chess/Rummy already track this state
  // from server pot — Carrom is now consistent. 0 outside of an
  // active match; the top bar hides the chip via potPayout==null.
  const [matchPotServer, setMatchPotServer] = useState<number>(0);
  // Bet tiers + platform fee come from the server via /api/carrom/config
  // on mount. The fallback constants are only the boot-time placeholder
  // until the fetch resolves. Earlier this page hard-coded
  // PLATFORM_FEE_FALLBACK in the payout calc and ignored what admin
  // had configured — so the on-screen WIN amount on each cash tile and
  // the in-match payout chip would diverge from the value the server
  // actually credits at settlement (the user-reported "win shows
  // different from what hits my wallet" bug on Carrom).
  const [betTiers, setBetTiers] = useState<number[]>(BET_TIERS_FALLBACK);
  const [platformFeePercent, setPlatformFeePercent] = useState<number>(PLATFORM_FEE_FALLBACK);
  // Concurrent-match modal state. The server returns
  // err='concurrent_match_limit' with a `blocking` array describing
  // which other game/queue/match the player is already in. Earlier
  // this branch just popped a hostile error toast, so the player had
  // no idea WHICH game they were in or how to resolve it. Mirrors the
  // pattern used by ludo / xo / rummy / chess Page.tsx.
  const [concBlocking, setConcBlocking] = useState<any[] | null>(null);
  const [concOpen,     setConcOpen]     = useState(false);
  // Live queue snapshot for the waiting modal. Server pushes
  // carrom_waiting_in_queue events whenever the queue's profile set
  // changes; capturing them here lets the modal swap from the
  // search-pulse placeholder to the opponent's real avatar + name as
  // soon as a bot or human is seated. Pre-fix the modal had the
  // placeholder hard-coded and ignored the server payload.
  const [queueProfiles, setQueueProfiles] = useState<{
    profiles: Array<{ userId?: string; name?: string; displayName?: string; avatarUrl?: string | null; isBot?: boolean; is_bot?: boolean }>;
    yourUserId: string | null;
  }>({ profiles: [], yourUserId: null });
  // Server tells us our seat via `carrom_you_are`. Until that lands
  // we tentatively render as p1 — visually identical because both
  // seats sit on the same baseline in 2P. Slice 4 always shuffles
  // seats server-side so the assignment is non-deterministic.
  const [yourSeat, setYourSeat] = useState<Seat>('p1');
  const [oppName, setOppName] = useState<string>('OPPONENT');
  const [oppAvatar, setOppAvatar] = useState<string | null>(null);
  // When the opponent is a bot, surface their difficulty mode as a
  // small visible tag on their profile card. Lets the tester confirm
  // at a glance which AI tier the matchmaking actually handed them
  // — the bot picker can override admin defaults via personality
  // weights, so "I set everything to hard" doesn't always guarantee
  // a hard bot at the table. `null` = human opponent, no tag.
  const [oppBotMode, setOppBotMode] = useState<string | null>(null);

  // Admin-tunable timing values, loaded from /api/carrom/config on
  // mount. The fallbacks match CarromGame defaults so the page
  // renders sensibly even before the config GET resolves; once it
  // does, every dependent timer (urgency beep lead, VS-intro hold,
  // match-end result pause) picks the live admin value up cleanly.
  const [turnSeconds, setTurnSeconds] = useState<number>(TURN_SECONDS_FALLBACK);
  const [urgencyBeepLeadSec, setUrgencyBeepLeadSec] = useState<number>(URGENCY_BEEP_LEAD_SEC_FALLBACK);
  const [vsIntroSeconds, setVsIntroSeconds] = useState<number>(VS_INTRO_SECONDS_FALLBACK);
  const [matchEndResultDelaySec, setMatchEndResultDelaySec] = useState<number>(MATCH_END_RESULT_DELAY_SEC_FALLBACK);

  // WS plumbing — connection, status, and the latest server-resolved
  // shot snapshot waiting to be applied at the end of the local
  // animation. See `handleShotResult` for the apply flow.
  const wsRef = useRef<CarromWS | null>(null);
  const voiceRef = useRef<WSVoice | null>(null);
  const [, setWsStatus] = useState<WSStatus>('connecting');
  const pendingShotRef = useRef<any | null>(null);
  // Opponent's live aim — populated by `carrom_aim_update`, cleared
  // by `carrom_aim_clear` or the next `carrom_shot_resolved`. The
  // Board renders a dashed dot strip for this so the viewer can see
  // exactly where the opponent is about to fire.
  const [opponentAim, setOpponentAim] =
    useState<{ angle: number; power: number; strikerX?: number | null } | null>(null);
  // Live lobby stats from the server's `carrom_global_stats`
  // broadcast — total online players (with the bot-fill drift the
  // server applies for theatre) and a humans-only-per-stake queue
  // count keyed by bet amount. The lobby renders these as the
  // online-pill at the top and a "N waiting" badge on each cash
  // tile.
  const [onlinePlayers, setOnlinePlayers] = useState<number>(0);
  const [waitingByBet, setWaitingByBet] = useState<Record<string, number>>({});
  const [showWaitingBadge, setShowWaitingBadge] = useState<boolean>(true);
  // Insufficient-balance modal — pops on a server-side
  // `insufficient_funds` error AND on the pre-flight wallet check
  // when the user taps a tile they can't afford. Same shared
  // component every other game uses; the CTA navigates to
  // /wallet for top-up.
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientBet, setInsufficientBet] = useState<number>(0);
  // True while the opponent's rAF replay is animating coins/striker
  // across the board. The slider thumb gates on this so it doesn't
  // gallop along with the in-flight puck — once the replay settles
  // and the canonical striker lands on the user's baseline, the
  // thumb snaps to that position cleanly.
  const [replayActive, setReplayActive] = useState(false);
  // Seat of the player whose striker is CURRENTLY in flight (local
  // sim or rAF replay). Drives the puck colour: while a shot is
  // animating, the striker keeps the SHOOTER's colour through to
  // the end of the animation, even though `currentTurn` (the seat
  // who shoots NEXT) flips immediately when the server's
  // shot_resolved arrives. Without this, firing a yellow striker
  // would turn it blue mid-board the moment the server replied —
  // exactly the bug the user reported.
  //   • null = no shot animating; colour by turn-holder.
  //   • <seat> = animating; colour by this seat's team.
  // Set by onShotFired (local) and applyServerShot (remote); cleared
  // by handleShotResult (local end) and rAF-tick end (remote end).
  const [shootingSeat, setShootingSeat] = useState<Seat | null>(null);
  // Recovery card — populated from /carrom/pending-result on mount
  // when the user has an unacknowledged previous-match result. The
  // localStorage `pendingResult.acked` set is consulted FIRST so a
  // user who already dismissed the card on this device never sees
  // it again, even if the server's `acknowledged_at` write was
  // lost in the wire (flaky network, auth blip, server crash). The
  // server-side persistence still backs everything up — the local
  // ack is just a defensive UX layer matching the pattern Ludo /
  // X&O / Rummy use.
  const [recovery, setRecovery] = useState<{
    result: 'win' | 'loss' | 'kick' | 'draw';
    payout: number;
    pot: number;
    betAmount: number;
    platformFee: number;
    yourCoins: number;
    oppCoins: number;
    queenWith: 'you' | 'opp' | 'none';
    opponents: Array<{ name?: string; seat?: number; team?: string; is_bot?: boolean }>;
    sessionId: number;
  } | null>(null);

  // The match-over popup is DEFERRED until the winning shot's
  // animation completes. When `applyShotGameState` sees gameOver,
  // it stashes the winner here instead of immediately flipping the
  // phase to 'result'. The animation tail (handleShotResult for a
  // local shot, or end-of-rAF for a remote one) reads this and
  // schedules the popup with a 2.5 s "appreciate" pause so the
  // user gets to see the last coin actually drop into the pocket
  // before the modal interrupts.
  const gameOverPendingRef = useRef<{ winnerSeat: Seat | null } | null>(null);
  // Live phase ref so async callbacks (pending-result delay timer)
  // can sniff the current phase without re-binding.
  const phaseRef = useRef<Phase>(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  // Live yourSeat ref so message handlers see the seat assigned at
  // match-start without re-creating the WS subscription.
  const yourSeatRef = useRef<Seat>(yourSeat);
  useEffect(() => { yourSeatRef.current = yourSeat; }, [yourSeat]);

  const [coins, setCoins] = useState<Coin[]>(() => initialLayout());
  const [striker, setStriker] = useState<Striker>(() => STRIKER_HOME('p1'));
  const [score, setScore] = useState({ you: 0, opp: 0 });
  const [fouls, setFouls] = useState({ you: 0, opp: 0 });
  // Queen lifecycle marker — server's `carrom_shot_resolved` updates
  // this; the page itself just feeds the value into state. The local
  // value is only consulted by the reconnect-sync handler today, so
  // the getter is unused outside the dispatch table — silenced via
  // the leading underscore.
  const [, setQueenStatus] = useState<'on-board' | 'pending-cover' | 'covered'>('on-board');
  // Who currently has the queen — only set once it's COVERED. The
  // profile cards display the queen icon next to whichever player
  // has it. Until covered (still pending) this stays null.
  const [queenWith, setQueenWith] = useState<'you' | 'opp' | null>(null);
  const [currentTurn, setCurrentTurn] = useState<Seat>('p1');
  // Live currentTurn ref. Same pattern as phaseRef / yourSeatRef above.
  // Why: `onMessage` is a useCallback with [] deps — its closure pins
  // every React-state read to the first-render value. The "your turn"
  // chime gate reads `currentTurn !== yourSeatRef.current` inside that
  // closure, which means the comparison ALWAYS uses the initial state
  // 'p1'. For a white player seated at p1 the condition collapses to
  // `'p1' !== 'p1'` = false, so the chime never fires for them. The
  // ref escape hatch gives the chime gate a live read.
  const currentTurnRef = useRef<Seat>(currentTurn);
  useEffect(() => { currentTurnRef.current = currentTurn; }, [currentTurn]);
  // Increments on every turn change (or shot completion in this
  // prototype). Used as the React `key` for the timer fill so the
  // animation restarts each turn.
  const [turnSeq, setTurnSeq] = useState(0);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [listenMuted, setListenMuted] = useState(false);
  const [quitConfirm, setQuitConfirm] = useState(false);
  const [matchOutcome, setMatchOutcome] = useState<'win' | 'loss' | null>(null);
  // Timestamp when the latest foul-overlay should appear. Used as
  // both a visibility flag (non-null = show the overlay) and a
  // React key so consecutive fouls re-trigger the animation
  // cleanly. Cleared automatically by a 1.5s timer.
  const [foulShownAt, setFoulShownAt] = useState<number | null>(null);
  // Specific reason for the current foul, shown as a subtitle below
  // the big "FOUL!" text. The user asked for the WHY ("striker
  // pocketed", "no contact", "wrong coin pocketed") to be visible
  // every time, instead of just a toast that's easy to miss when
  // the player's eyes are on the board. Cleared in lockstep with
  // `foulShownAt` so the subtitle vanishes with the overlay.
  const [foulReason, setFoulReason] = useState<string | null>(null);
  // Whose foul is it — "you" or "opp" — drives the colour accent on
  // the subtitle so a glance tells the user which side fouled.
  const [foulIsMine, setFoulIsMine] = useState<boolean>(false);
  // 2026-05-20 — Foul *category* drives a distinct visual look so
  // players instantly tell apart "I pocketed my own striker" (striker
  // category, red dramatic icon) from "I knocked opponent's coin in"
  // (coin category, amber warning icon). Different category = different
  // mistake = different fix needed next shot.
  type FoulCategory = 'striker' | 'coin' | 'no_contact' | null;
  const [foulCategory, setFoulCategory] = useState<FoulCategory>(null);
  // Phase 24 — queen-cover banner. Time-tagged so consecutive covers
  // (rare but possible across a long PvP) re-trigger the animation.
  // Carries the actor side so the banner reads either "YOU COVERED
  // THE QUEEN! +3" or "OPPONENT COVERED THE QUEEN! +3".
  const [queenCoveredAt, setQueenCoveredAt] = useState<number | null>(null);
  const [queenCoveredByMe, setQueenCoveredByMe] = useState<boolean>(false);

  const matchTimers = useRef<number[]>([]);
  const clearMatchTimers = () => {
    for (const t of matchTimers.current) window.clearTimeout(t);
    matchTimers.current = [];
  };
  useEffect(() => () => clearMatchTimers(), []);

  // Sound-enable toggle from the settings menu — pipes through to
  // the sounds module so the urgency beep, collision clicks, foul
  // buzzer, and pocket-drop thuds all respect a single switch.
  useEffect(() => {
    setGameSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  // Install a one-shot document-level listener so the very first
  // user interaction anywhere on the page unlocks the AudioContext.
  // Carrom's per-shot sounds (collision clicks, pocket drops) are
  // fired from `requestAnimationFrame` callbacks which are NOT
  // user-gestures — without this unlock the browser silently drops
  // them. Belt-and-suspenders alongside `primeAudio()` in startMatch.
  useEffect(() => {
    installAudioUnlock();
  }, []);

  // Auto-dismiss the foul overlay 1500ms after it appears. Clear the
  // reason subtitle AND category at the same moment so stale state
  // can never resurface on a future foul.
  useEffect(() => {
    if (foulShownAt == null) return;
    const t = window.setTimeout(() => {
      setFoulShownAt(null);
      setFoulReason(null);
      setFoulCategory(null);
    }, 1500);
    return () => window.clearTimeout(t);
  }, [foulShownAt]);
  // Phase 24 — auto-dismiss the queen-covered banner after 2s. Slightly
  // longer than the foul (which is a "you screwed up" message and
  // should clear fast) because the cover banner is a celebratory
  // milestone — the user wants a beat to register the +3.
  useEffect(() => {
    if (queenCoveredAt == null) return;
    const t = window.setTimeout(() => setQueenCoveredAt(null), 2000);
    return () => window.clearTimeout(t);
  }, [queenCoveredAt]);

  // Urgency beep — same end-of-turn tone Ludo uses, fires when the
  // turn timer is close to expiring. The bar animates over the full
  // TURN_SECONDS window; urgency kicks in URGENCY_BEEP_LEAD_SEC before
  // the server-side timeout so the last few seconds of every turn
  // audibly count down. The beep fires for whichever player's turn
  // is active, so on the local device the player hears their own
  // AND their opponent's urgency just like in Ludo. Cleared on phase
  // change, on turn change, on unmount, and when the user mutes
  // sound.
  useEffect(() => {
    if (phase !== 'playing' || !soundEnabled) {
      stopUrgencyBeep();
      return;
    }
    const leadMs = Math.max(0, (turnSeconds - urgencyBeepLeadSec) * 1000);
    const t = window.setTimeout(() => startUrgencyBeep(), leadMs);
    return () => {
      window.clearTimeout(t);
      stopUrgencyBeep();
    };
  }, [turnSeq, phase, soundEnabled, turnSeconds, urgencyBeepLeadSec]);

  // ─── WS lifecycle + message handler ───────────────────────────
  // Connect once on mount, reconnect under the hood (CarromWS handles
  // the retry cadence). Every server frame is routed through
  // `onMessage` which mutates the local React state to match. The
  // closure captures stable refs only — `yourSeatRef` / `phaseRef` —
  // so we never have to rebuild the listener as state changes.
  const onMessage = useCallback((msg: any) => {
    if (!msg || typeof msg !== 'object') return;
    switch (msg.type) {
      case 'carrom_auth_success':
        // Server is ready — flip the bootstrap gate so the loading
        // overlay can release once asset preload has also completed.
        // Lobby renders straight away after that; the join_queue
        // happens on bet-tile tap.
        setAuthReady(true);
        break;

      case 'carrom_global_stats':
        // Lobby online-count + per-stake "N waiting" humans, broadcast
        // every ~2 s by the server's heartbeat (and on every queue
        // join/leave). The lobby renders both — pill at the top of
        // the screen, badges on each affected cash tile.
        setOnlinePlayers(Number(msg.online_players || 0) | 0);
        setWaitingByBet(
          (msg.waiting_by_bet && typeof msg.waiting_by_bet === 'object')
            ? msg.waiting_by_bet
            : {},
        );
        setShowWaitingBadge(!!msg.show_waiting_badge);
        break;

      case 'carrom_waiting_in_queue':
        // Waiting modal stays up until carrom_match_start lands. The
        // payload also carries `profiles` (an array of who's currently
        // seated in the queue) and `yourUserId`. Pre-fix the payload
        // was discarded and the modal was stuck on the "your face +
        // search-pulse placeholder" visual for the entire wait, even
        // after a bot/opponent had been seated. Capture the snapshot
        // so the modal can hot-swap to the real opponent's avatar +
        // name immediately.
        setQueueProfiles({
          profiles: Array.isArray(msg.profiles) ? msg.profiles : [],
          // Server emits the local-player id in snake_case
          // (`your_user_id`). Plan D originally read camelCase
          // (`yourUserId`), which left this null and made the
          // opponent-find lambda in the modal match every profile —
          // surfacing the local player as "OPPONENT" with their own
          // avatar+name. Read the snake-case form first; keep camelCase
          // as a defensive fallback.
          yourUserId: (msg.your_user_id || msg.yourUserId)
            ? String(msg.your_user_id || msg.yourUserId)
            : null,
        });
        if (phaseRef.current === 'lobby') setPhase('queue');
        break;

      case 'carrom_session_aborted':
        toast.push('error',
          msg.note || 'Match could not start — no money was taken.',
          2400);
        setPhase('lobby');
        break;

      case 'carrom_left_game_success':
        // Stop voice on the way back to the lobby — a fresh match
        // calls startVoice() again on its match_start frame.
        try { voiceRef.current?.stop(); } catch {}
        voiceRef.current = null;
        setOppBotMode(null);
        gameOverPendingRef.current = null;
        setPhase('lobby');
        break;

      case 'wallet_update':
        // The wallet store is hydrated by the same socket events Ludo
        // uses — Carrom's WS just forwards the same `wallet_update`
        // type. The `useWallet` hook subscribes to /wallet refreshes
        // independently, so we let it pick the new balance up.
        break;

      case 'carrom_you_are': {
        const s = seatNumToKey(Number(msg.seat));
        setYourSeat(s);
        yourSeatRef.current = s;
        break;
      }

      case 'carrom_match_start': {
        // Reset the board, score, fouls, queen status — the engine's
        // initial state is canonical and the client mirrors it.
        // Also drop any stale gameOver queued from the previous
        // match (defensive — the previous match already fired its
        // popup via the animation tail).
        gameOverPendingRef.current = null;
        setCoins(initialLayout());
        setStriker(STRIKER_HOME(yourSeatRef.current));
        setScore({ you: 0, opp: 0 });
        setFouls({ you: 0, opp: 0 });
        setQueenStatus('on-board');
        setQueenWith(null);
        setCurrentTurn(seatNumToKey(Number(msg.currentTurn)));
        setTurnSeq(0);
        setMatchOutcome(null);
        // Pull the opponent's display name + avatar straight off the
        // match_start profiles map. Also pick up `botMode` (only set
        // for bot opponents) so the profile card can render the
        // difficulty tag for at-a-glance test visibility.
        const profiles = msg.playerProfiles || {};
        const myNum = seatKeyToNum(yourSeatRef.current);
        for (const seatKey of Object.keys(profiles)) {
          const seatNum = Number(seatKey);
          if (seatNum === myNum) continue;
          const p = profiles[seatKey];
          setOppName((p.displayName || 'OPPONENT').toUpperCase());
          setOppAvatar(p.avatarUrl || null);
          setOppBotMode(p.isBot ? (p.botMode || null) : null);
        }
        if (typeof msg.bet === 'number') setSelectedBet(msg.bet);
        // Authoritative pot from the server. The top-bar's WIN
        // chip + the result popup both derive their amounts from
        // this. See `matchPotServer` declaration for full rationale.
        if (typeof msg.pot === 'number') setMatchPotServer(msg.pot);
        // Voice chat — kick off the WSVoice instance now that we know
        // our seat. The mic was prewarmed in startMatch (Rule 2); the
        // server's relay enforces the AUDIO_ENABLED kill-switch
        // (Rule 5), so we don't gate on config here.
        startVoice(yourSeatRef.current);
        // VS splash hold — admin-tunable via VS_INTRO_SECONDS so
        // operators can lengthen it for production audiences.
        setPhase('vs');
        const vsHoldMs = Math.max(0, vsIntroSeconds * 1000);
        matchTimers.current.push(window.setTimeout(() => setPhase('playing'), vsHoldMs));
        break;
      }

      case 'carrom_shot_resolved': {
        // The shot itself implicitly clears the aim preview — once
        // the puck is in flight there's nothing to aim. Drop the
        // opponent's stale aim so the dashed dot strip vanishes.
        setOpponentAim(null);

        // Game state — score / fouls / queen / currentTurn / gameOver
        // — applies IMMEDIATELY for both shooter paths so the user's
        // turn timer starts the moment the server says it's their
        // turn, instead of waiting for the 1–5 s replay to finish.
        applyShotGameState(msg);

        const myNum = seatKeyToNum(yourSeatRef.current);
        if (Number(msg.seat) === myNum) {
          // Local shooter — Board.tsx is mid-animation showing
          // simCoins overlay. Stash the server snapshot;
          // `handleShotResult` will apply finalCoins/finalStriker
          // when Board's local sim ends. (Game state already
          // applied above.)
          pendingShotRef.current = msg;
        } else {
          // Remote shooter — replay the server's timeline visually.
          // applyServerShot calls applyShotGameState again as a
          // belt-and-braces no-op; the second call sets the same
          // values back (idempotent).
          applyServerShot(msg);
        }
        break;
      }

      case 'carrom_aim_update':
        // Opponent is dragging their slingshot (or the bot is
        // emitting its synthetic aim preview). Store the live aim
        // so Board.tsx can render their dashed-dot preview at the
        // active striker. Self-message guard: the server already
        // skips the sender, so receiving this means it's the other
        // seat's aim every time.
        setOpponentAim({
          angle: Number(msg.angle),
          power: Number(msg.power),
          strikerX: typeof msg.strikerX === 'number' ? msg.strikerX : null,
        });
        // Hard bots can REPOSITION the striker as part of their
        // decision — they sample several baseline x values and pick
        // the one with the cleanest shot. The aim_update is the
        // FIRST frame the receiver sees with the new x; without
        // applying it, the aim arrow renders from the OLD striker
        // position (pointing at nothing useful) and a millisecond
        // before the shot fires the puck visibly teleports to the
        // new x. Snapping the canonical striker to msg.strikerX
        // here keeps the aim, the puck, and the eventual timeline
        // playback all originating from one consistent point.
        // Humans don't reposition during slingshot drag (the slider
        // sets x BEFORE drag starts), so this is a no-op for human
        // aim previews.
        if (typeof msg.strikerX === 'number' && Number.isFinite(msg.strikerX)) {
          setStriker((prev) => {
            if (Math.abs(prev.x - msg.strikerX) < 0.001) return prev;
            return { ...prev, x: msg.strikerX };
          });
        }
        break;

      case 'carrom_aim_clear':
        // Opponent canceled their drag without firing — hide arrow.
        setOpponentAim(null);
        break;

      case 'carrom_striker_move':
        // Opponent's puck is sliding along the rail — bot adjusting
        // its launch position before aiming, OR a human dragging
        // their slider. The aim arrow is NOT shown during this
        // phase (mirrors how a real player can either move the
        // slider OR aim, never both at once). Snap the canonical
        // striker x to the broadcast value and ensure no leftover
        // aim arrow is visible.
        if (typeof msg.x === 'number' && Number.isFinite(msg.x)) {
          setStriker((prev) => {
            if (Math.abs(prev.x - msg.x) < 0.001) return prev;
            return { ...prev, x: msg.x };
          });
          setOpponentAim(null);
        }
        break;

      case 'carrom_turn_update':
        // The server broadcasts this on a turn timeout (the only path
        // that doesn't already ride a `carrom_shot_resolved`). The
        // payload's `striker` is on the NEW shooter's baseline —
        // canonical y=0.86 for white seats, y=0.14 for black —
        // so we must replace the striker WHOLE, not merge only x.
        // Merging only x kept the puck stuck on the timed-out
        // player's side: their canonical baseline persisted, the
        // new shooter saw their controls activate but the striker
        // remained on the opponent's end of the board.
        setOpponentAim(null);
        if (typeof msg.currentTurn === 'number') {
          const nextSeat = seatNumToKey(msg.currentTurn);
          // Chime when the timeout-pass hands the turn TO us. Read
          // currentTurn via the ref — the surrounding `onMessage` is
          // a useCallback([]) so a direct `currentTurn` read is
          // pinned to the initial state and would mis-fire (e.g. a
          // white player at p1 sees `'p1' !== 'p1'` and the chime
          // is suppressed forever).
          if (nextSeat === yourSeatRef.current
              && currentTurnRef.current !== yourSeatRef.current) {
            try { playYourTurnChime(); } catch {}
          }
          setCurrentTurn(nextSeat);
          setTurnSeq((t) => t + 1);
        }
        if (msg.striker) {
          setStriker(msg.striker);
        }
        // Server may include refreshed queen + coins state when a
        // pending-cover obligation is reset by the timeout (the
        // engine snaps the queen back to centre and clears the
        // 'pending-cover' status so the new shooter starts from a
        // clean queen-on-board state). Apply both — without the
        // coins refresh, the queen would visually stay sunk on the
        // client even though the server has already returned her.
        if (Array.isArray(msg.coins)) setCoins(msg.coins);
        if (typeof msg.queenStatus === 'string') {
          setQueenStatus(msg.queenStatus);
        }
        if ('queenWith' in msg) {
          const myNum = seatKeyToNum(yourSeatRef.current);
          const myTeam = (myNum === 1 || myNum === 3) ? 'white' : 'black';
          if (!msg.queenWith)                setQueenWith(null);
          else if (msg.queenWith === myTeam) setQueenWith('you');
          else                                setQueenWith('opp');
        }
        break;

      case 'carrom_turn_timeout':
        // Server-side strike — show a brief toast for the local player.
        if (Number(msg.seat) === seatKeyToNum(yourSeatRef.current)) {
          toast.push('error', `Turn timed out (${msg.strikes}/3)`, 1800);
        } else {
          toast.push('info', 'Opponent timed out', 1400);
        }
        break;

      case 'carrom_player_disconnected':
        if (Number(msg.seat) !== seatKeyToNum(yourSeatRef.current)) {
          toast.push('info', 'Opponent disconnected — waiting…', 1800);
        }
        break;

      case 'carrom_player_reconnected':
        if (Number(msg.seat) !== seatKeyToNum(yourSeatRef.current)) {
          toast.push('success', 'Opponent reconnected', 1400);
        }
        break;

      case 'carrom_player_kicked': {
        const meKicked = Number(msg.seat) === seatKeyToNum(yourSeatRef.current);
        try { voiceRef.current?.stop(); } catch {}
        voiceRef.current = null;
        if (meKicked) {
          setMatchOutcome('loss');
          setPhase('result');
        } else {
          // Opponent forfeited — local player wins by default.
          setMatchOutcome('win');
          setPhase('result');
        }
        break;
      }

      case 'carrom_match_over': {
        // The server fires this RIGHT AFTER `carrom_shot_resolved`
        // when a winning shot resolves. If we already queued a
        // game-over via the shot path (gameOverPendingRef set),
        // let the animation tail fire the popup at the right time
        // — don't double-fire here. The OTHER path that produces a
        // match_over without a preceding shot_resolved is a kick
        // (the kicked player forfeits), which doesn't have an
        // animation to wait for, so we fire the popup immediately.
        const winnerSeat = msg.winnerSeat == null
          ? null
          : seatNumToKey(Number(msg.winnerSeat));
        const won = winnerSeat === yourSeatRef.current;
        if (gameOverPendingRef.current) {
          // Animation-tail path will handle the popup. Just store
          // the winner in case the server's shot_resolved had a
          // null winnerSeat (defensive — shouldn't happen).
          gameOverPendingRef.current = { winnerSeat };
        } else {
          try { voiceRef.current?.stop(); } catch {}
          voiceRef.current = null;
          setMatchOutcome(won ? 'win' : 'loss');
          setPhase('result');
        }
        break;
      }

      case 'carrom_session_paused':
        toast.push('info', 'Match paused — waiting for opponent', 1800);
        break;

      case 'carrom_session_resumed':
        toast.push('success', 'Match resumed', 1400);
        break;

      case 'carrom_session_ended':
        if (msg.reason === 'abandoned') {
          toast.push('info', 'Match abandoned — refund issued', 2200);
          setPhase('lobby');
        }
        break;

      case 'refund_issued':
        toast.push('info', `Refunded ${msg.amount}`, 1800);
        break;

      case 'carrom_reconnect_sync': {
        // Full state restore after a socket blip. Hydrate everything
        // from the snapshot and switch straight to the playing phase.
        const mySeat = seatNumToKey(Number(msg.yourSeat));
        setYourSeat(mySeat);
        yourSeatRef.current = mySeat;
        if (Array.isArray(msg.coins)) setCoins(msg.coins);
        if (msg.striker) setStriker(msg.striker);
        if (typeof msg.currentTurn === 'number') {
          setCurrentTurn(seatNumToKey(msg.currentTurn));
        }
        // Re-anchor the bet + pot to the server's snapshot. Without
        // this, a mid-match refresh would render the top-bar WIN
        // chip from the stale `selectedBet` left over in the lobby
        // state — the user-reported "pot amount on top is not
        // correct" symptom. The server is the source of truth.
        if (typeof msg.bet === 'number') setSelectedBet(msg.bet);
        if (typeof msg.pot === 'number') setMatchPotServer(msg.pot);
        // Re-hydrate opponent identity (name + avatar + bot-mode tag)
        // from the playerProfiles map. carrom_match_start does the
        // same lookup; the reconnect_sync handler used to skip it,
        // which left oppName='OPPONENT' / oppAvatar=null on every
        // mid-match refresh — the user-reported "opponent's photo
        // and name disappear after refresh" bug. Mirrors XO's
        // xo_reconnect_sync handler, which already does this.
        const profiles = msg.playerProfiles || {};
        const myNum = seatKeyToNum(mySeat);
        for (const seatKey of Object.keys(profiles)) {
          const seatNum = Number(seatKey);
          if (seatNum === myNum) continue;
          const p = profiles[seatKey];
          setOppName((p.displayName || 'OPPONENT').toUpperCase());
          setOppAvatar(p.avatarUrl || null);
          setOppBotMode(p.isBot ? (p.botMode || null) : null);
        }
        // Score: own coinsSunk vs opp coinsSunk.
        const players = msg.players || {};
        const myNum2 = seatKeyToNum(mySeat);
        let yourCoins = 0, oppCoins = 0;
        let yourFouls = 0, oppFouls = 0;
        for (const k of Object.keys(players)) {
          const p = players[k];
          if (Number(p.seat) === myNum2) {
            yourCoins = p.coinsSunk | 0; yourFouls = p.fouls | 0;
          } else {
            oppCoins = p.coinsSunk | 0; oppFouls = p.fouls | 0;
          }
        }
        setScore({ you: yourCoins, opp: oppCoins });
        setFouls({ you: yourFouls, opp: oppFouls });
        setQueenStatus(msg.queenStatus || 'on-board');
        // Translate the global queen-with into the local 'you'/'opp' tag.
        const myTeam = (myNum2 === 1 || myNum2 === 3) ? 'white' : 'black';
        if (!msg.queenWith)            setQueenWith(null);
        else if (msg.queenWith === myTeam) setQueenWith('you');
        else                                setQueenWith('opp');
        setTurnSeq((t) => t + 1);
        setPhase('playing');
        break;
      }

      case 'voice_chunk':
        // Server-relayed PCM frame from another seat. WSVoice
        // schedules it onto its playback context (Rule 8 — gapless
        // back-to-back) and skips frames addressed to ourselves.
        voiceRef.current?.onRemoteChunk(Number(msg.from), String(msg.pcm));
        break;

      case 'rtc_signal':
        // Reserved for the WebRTC fallback path. The WS-relay
        // transport doesn't use RTC signalling at all, so this is a
        // no-op today; kept so a future fallback can plug in here.
        break;

      case 'error':
        if (msg.code === 'insufficient_funds') {
          // Drop back to the lobby and show the shared recharge
          // prompt — same UX every other game uses on this error.
          setPhase('lobby');
          setInsufficientBet(selectedBet || 0);
          setInsufficientOpen(true);
        } else if (msg.code === 'concurrent_match_limit') {
          // Open the friendly modal instead of the hostile error toast
          // the previous code shipped. The modal shows blocking match
          // details (which game, how much at stake) + a Quit button so
          // the player can resolve the conflict in one tap. Same shape
          // as ludo/xo/rummy/chess.
          setConcBlocking(Array.isArray(msg.blocking) ? msg.blocking : []);
          setConcOpen(true);
          setPhase('lobby');
        } else if (msg.code === 'daily_loss_cap') {
          toast.push('error', 'Daily loss cap reached — try again tomorrow', 2600);
          setPhase('lobby');
        } else if (msg.code === 'bet_limit') {
          toast.push('error', msg.message || 'This bet is above your limit — contact your agent.', 2600);
          setPhase('lobby');
        } else if (msg.code === 'auth_failed') {
          toast.push('error', 'Authentication failed', 2200);
        } else if (msg.code) {
          toast.push('error', String(msg.code).replaceAll('_', ' '), 1800);
        }
        break;

      default:
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the WS once and keep it open for the entire page lifetime.
  // Pending-result lookup is gated behind a 1.2s race-guard against
  // `carrom_reconnect_sync` so a mid-match reconnect doesn't briefly
  // flash the "previous match result" popup.
  useEffect(() => {
    let closed = false;
    (async () => {
      // Fetch admin-tunable config first so the visual timer bar,
      // urgency-beep lead, VS-intro hold, and match-end pause all
      // use live admin values instead of the boot-time fallbacks.
      // Errors are non-fatal — the fallbacks already mirror the
      // server defaults, so the page works without this resolving.
      try {
        const cfg = await api.get<{
          // Bet tiles + fee — drive the cash-tile WIN labels and the
          // in-match payout chip. Previously these were ignored and
          // the page rendered with the hardcoded fallback constants
          // (BET_TIERS_FALLBACK / PLATFORM_FEE_FALLBACK), which
          // diverged from admin-edited values and produced the
          // user-visible "win amount on tile != amount credited at
          // settlement" bug.
          betTiers?: number[];
          platformFeePercent?: number;
          turnSeconds?: number;
          urgencyBeepLeadSeconds?: number;
          vsIntroSeconds?: number;
          matchEndResultDelaySeconds?: number;
        }>('/carrom/config');
        if (!closed && cfg) {
          if (Array.isArray(cfg.betTiers) && cfg.betTiers.length) {
            setBetTiers(cfg.betTiers);
            // If the boot-time default tier isn't in the admin-set
            // list anymore, snap selectedBet to the first valid tier
            // so the visible "selected" highlight stays meaningful.
            setSelectedBet((cur) =>
              cfg.betTiers!.includes(cur) ? cur : cfg.betTiers![0]);
          }
          if (typeof cfg.platformFeePercent === 'number') {
            setPlatformFeePercent(cfg.platformFeePercent);
          }
          if (typeof cfg.turnSeconds === 'number')              setTurnSeconds(cfg.turnSeconds);
          if (typeof cfg.urgencyBeepLeadSeconds === 'number')   setUrgencyBeepLeadSec(cfg.urgencyBeepLeadSeconds);
          if (typeof cfg.vsIntroSeconds === 'number')           setVsIntroSeconds(cfg.vsIntroSeconds);
          if (typeof cfg.matchEndResultDelaySeconds === 'number') setMatchEndResultDelaySec(cfg.matchEndResultDelaySeconds);
        }
      } catch {
        // Config endpoint failed — fall back to in-component defaults.
      }
      try {
        const pr = await api.get<{ pending: any }>('/carrom/pending-result');
        if (!closed && pr?.pending) {
          // localStorage-backed dedupe — if the user already
          // dismissed this session's card on THIS device but the
          // server's acknowledge POST failed in the past, retry
          // it silently in the background and DO NOT show the
          // popup again. Matches the pattern Ludo / X&O / Rummy
          // already use.
          if (isSessionAcked('carrom', pr.pending.sessionId)) {
            api.post('/carrom/acknowledge-result', { sessionId: pr.pending.sessionId })
              .then(() => clearSessionAcked('carrom', pr.pending.sessionId))
              .catch(() => {});
          } else {
            // 1.2 s race-guard against `carrom_reconnect_sync` —
            // a mid-match reconnect arriving in this window flips
            // the phase out of 'lobby' before we surface the card.
            // Without that guard, a user with an open session but
            // a brief WS blip would see "previous match result"
            // briefly flash before being dropped back into the
            // live board.
            setTimeout(() => {
              if (closed) return;
              if (phaseRef.current !== 'lobby') return;
              const p = pr.pending;
              setRecovery({
                result:      p.result,
                payout:      Number(p.payout      || 0) | 0,
                pot:         Number(p.pot         || 0) | 0,
                betAmount:   Number(p.betAmount   || 0) | 0,
                platformFee: Number(p.platformFee || 0) | 0,
                yourCoins:   Number(p.yourCoins   || 0) | 0,
                oppCoins:    Number(p.oppCoins    || 0) | 0,
                queenWith:   (p.queenWith === 'you' || p.queenWith === 'opp')
                  ? p.queenWith
                  : 'none',
                opponents:   Array.isArray(p.opponents) ? p.opponents : [],
                sessionId:   Number(p.sessionId) | 0,
              });
            }, 1200);
          }
        }
      } catch {}
      const ws = new CarromWS();
      ws.on(onMessage);
      ws.onStatus(setWsStatus);
      ws.connect();
      wsRef.current = ws;
    })();
    return () => {
      closed = true;
      wsRef.current?.close();
      wsRef.current = null;
      // Tear down voice on unmount too — the route may switch back
      // to the lobby and we don't want a dangling capture pipeline
      // sending frames into a closed socket.
      try { voiceRef.current?.stop(); } catch {}
      voiceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Voice chat (WS-relay transport) ─────────────────────────
  // Cookbook Rules: 1 (per-game key + module), 4 (start() only — never
  // also attachLocalStream from here), 5 (no config gate; the server
  // honours AUDIO_ENABLED via relayVoiceChunk).
  function startVoice(mine: Seat | null) {
    if (!mine) return;
    voiceRef.current?.stop();
    voiceRef.current = null;
    const v = new WSVoice(mine, (m) => { wsRef.current?.send(m); });
    if (micMuted)    v.setMuted(true);
    if (listenMuted) v.setListenMuted(true);
    voiceRef.current = v;
    v.start().then((ok) => {
      if (!ok) {
        toast.push('error', 'Mic blocked — check browser permissions');
      } else {
        // Cookbook §4 step 11 acceptance — single toast, neutral copy.
        toast.push('success', 'Voice chat enabled', 1800);
      }
    });
  }

  // ─── Matchmaking flow (real WS) ───────────────────────────────
  function startMatch(bet: number) {
    setSelectedBet(bet);
    // Bet-tile tap is a user gesture — unlock the AudioContext so
    // collision/pocket/foul sounds work later from rAF callbacks
    // (which are NOT gesture contexts).
    primeAudio();
    if (bet > 0 && wallet < bet) {
      // Pre-flight check — open the shared recharge prompt right
      // away instead of letting the WS round-trip just to bounce
      // back. Server still validates authoritatively on
      // join_queue, so this is a UX shortcut, not the only gate.
      setInsufficientBet(bet);
      setInsufficientOpen(true);
      return;
    }
    // Cookbook Rule 2 — prewarm the mic INSIDE the click handler.
    // iOS Safari and most mobile Chromes only grant getUserMedia in a
    // gesture context. The carrom_match_start frame lands seconds
    // later in an async WS callback, which is not a gesture; if we
    // wait until then the permission prompt never appears and voice
    // is dead. Stash the live MediaStream on window.__carromMicStream
    // (Rule 1) so WSVoice.start() picks it up without a second ask
    // (Rule 3). No toast here — startVoice fires the single
    // "Voice chat enabled" toast on success, so the click feels like
    // one beat instead of two competing notifications.
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => { (window as any).__carromMicStream = stream; })
        .catch((e) => { console.warn('[carrom/voice] mic permission declined', e); });
    }
    const sent = wsRef.current?.send({
      type: 'carrom_join_queue',
      bet,
      playerCount: PLAYER_COUNT,
    });
    if (!sent) {
      toast.push('error', 'Connecting to server…', 2000);
      return;
    }
    setPhase('queue');
  }

  function cancelQueue() {
    wsRef.current?.send({ type: 'carrom_leave_game' });
    setPhase('lobby');
  }

  // ─── Apply an authoritative shot snapshot from the server ──────
  // The server's `carrom_shot_resolved` is the single source of truth
  // for coin/striker positions, the next turn, per-seat scores, fouls,
  // queen state, and the gameOver flag. This helper runs in two
  // contexts:
  //
  //   1. We just fired the shot. Board.tsx ran a local sim as a
  //      smooth preview; when its onShotResult callback fires we
  //      pull the stashed server frame and apply it here. The screen
  //      doesn't flicker because the simCoins overlay was hiding the
  //      stale parent state during animation.
  //
  //   2. The OPPONENT fired. There's no local sim, so the message
  //      handler calls this directly. Coins teleport to the server's
  //      final positions — fine for the MVP. Server-driven timeline
  //      animation lands in a later slice (replay.ts).
  // rAF handle for the in-flight opponent-shot replay, so a chain
  // of fast shots can cancel the previous animation before starting
  // the next one.
  const replayRafRef = useRef<number | null>(null);

  // Pull score / fouls / queen / currentTurn / foul overlay / win
  // detection out of `applyServerShot` so they can be applied the
  // INSTANT a `carrom_shot_resolved` lands — without waiting for the
  // 1–5 s replay animation to finish. The original design gated all
  // game-state updates behind the rAF loop's `applyServerShotFinal`
  // call at the end of replay, which meant the user's turn timer
  // didn't start counting until ~3 s into their actual turn (server
  // had already moved on, but the client UI lagged behind). Splitting
  // the apply in two — game state immediate, visual coins/striker
  // animated — fixes the "my turn doesn't come till the timer is
  // almost out" symptom.
  function applyShotGameState(snap: any) {
    if (!snap) return;
    const myNum = seatKeyToNum(yourSeatRef.current);
    const shooterIsMe = Number(snap.seat) === myNum;
    const players = snap.players || {};
    let yourCoins = 0, oppCoins = 0, yourFouls = 0, oppFouls = 0;
    for (const k of Object.keys(players)) {
      const p = players[k];
      if (Number(p.seat) === myNum) {
        yourCoins = p.coinsSunk | 0; yourFouls = p.fouls | 0;
      } else {
        oppCoins = p.coinsSunk | 0; oppFouls = p.fouls | 0;
      }
    }
    setScore({ you: yourCoins, opp: oppCoins });
    setFouls({ you: yourFouls, opp: oppFouls });
    setQueenStatus(snap.queenStatus || 'on-board');
    const myTeam = (myNum === 1 || myNum === 3) ? 'white' : 'black';
    if (!snap.queenWith)                setQueenWith(null);
    else if (snap.queenWith === myTeam) setQueenWith('you');
    else                                setQueenWith('opp');
    if (typeof snap.currentTurn === 'number') {
      const nextSeat = seatNumToKey(snap.currentTurn);
      // Your-turn audio cue. Two flavours so the ear can tell apart
      // "turn just came to you from the opponent" from "you sank a
      // coin and get to shoot again":
      //
      //   • Full chime (D5 → A5)  — the turn just FLIPPED toward us.
      //     A clear "wake up, your move" alert.
      //   • Retain chime (A5)     — we already had the turn AND we
      //     kept it (a coin sank). Quieter single note so a streak
      //     of sinks doesn't get noisy, while still telling the
      //     player "board is yours again, take the shot".
      //
      // currentTurnRef is read instead of `currentTurn` because
      // `onMessage` (which transitively invokes this function via
      // the case branches) is a useCallback([]) — its closure pins
      // every React-state read to the first-render value. Reading
      // `currentTurn` directly would freeze the comparison at 'p1'
      // and silently suppress the chime for a white player seated
      // at p1 forever (the original bug we already fixed for the
      // cross-turn flavour, and which would also affect the retain
      // branch if we read state directly here).
      if (nextSeat === yourSeatRef.current && !snap.gameOver) {
        if (currentTurnRef.current !== yourSeatRef.current) {
          // Cross-turn — opponent → us.
          try { playYourTurnChime(); } catch {}
        } else if (shooterIsMe) {
          // Retain — we shot, sank a coin, and the server kept the
          // turn on our seat. The `shooterIsMe` guard prevents a
          // false retain-chime when the snapshot lands without a
          // turn change for any other reason (e.g. a server keep-
          // alive that re-broadcasts state). On a real retain the
          // shooter is always us.
          try { playYourTurnAgainChime(); } catch {}
        }
      }
      setCurrentTurn(nextSeat);
    }
    setTurnSeq((t) => t + 1);

    // Foul overlay + sound — fires for BOTH shooter and receiver so
    // the user sees what happened regardless of who shot. Previously
    // the overlay was gated on `shooterIsMe`, which meant a foul on
    // the bot's shot never showed any visible foul indicator on the
    // human's screen unless the bot specifically pocketed the
    // striker (and even THAT relied on the rAF replay's per-event
    // trigger, which used a mis-named event type and didn't fire
    // either — see physics.js `striker-sunk` vs Page.tsx `sink+striker`).
    // Showing the FOUL overlay to both sides on every foul reason
    // (striker / no_contact / opp_coin_only) makes the rule visible
    // and matches how the user expects the game to communicate.
    const cls = snap.classified || {};
    if (cls.foul) {
      // Big centred FOUL overlay for ANY foul, both players, with a
      // human-readable subtitle saying WHY. The user asked for the
      // reason to always be visible — toasts alone are easy to miss
      // when eyes are on the board. The same map drives both the
      // overlay subtitle and the corner toast so the wording is
      // consistent. The audible buzzer fires on striker-pocket only
      // (it's the loudest foul); the quieter no_contact and
      // opp_coin_only fouls show the overlay + toast without the
      // jarring buzz.
      const foulText = (() => {
        if (cls.foulReason === 'striker') {
          return shooterIsMe
            ? 'Your striker pocketed'
            : 'Opponent pocketed their striker';
        }
        if (cls.foulReason === 'no_contact') {
          return shooterIsMe
            ? 'Striker hit no coin'
            : 'Opponent hit no coin';
        }
        if (cls.foulReason === 'opp_coin_only') {
          return shooterIsMe
            ? "You pocketed opponent's coin"
            : 'Opponent pocketed your coin';
        }
        return 'Foul';
      })();
      setFoulShownAt(Date.now());
      setFoulReason(foulText);
      setFoulIsMine(shooterIsMe);
      // Map server reason → visual category. striker + no_contact share
      // the "striker" visual (dramatic red, striker icon) because both
      // are "you mishandled the cue". opp_coin_only is the "coin"
      // category (amber warning, coin icon) because it's about the
      // wrong target being knocked in.
      const cat: 'striker' | 'coin' | 'no_contact' =
        cls.foulReason === 'opp_coin_only' ? 'coin'
        : cls.foulReason === 'no_contact'  ? 'no_contact'
        : 'striker';
      setFoulCategory(cat);
      if (cls.foulReason === 'striker') playFoul();
      toast.push('error', foulText, 1800);
    }
    // Phase 24 — queen-cover banner. Fires on the exact shot where
    // the queen transitions from un-covered to covered. Standard MCF
    // rule: covering side gets +3 bonus points (tracked server-side
    // on queenBonusPoints; banner is the immediate visual feedback).
    if (cls.queenJustCovered) {
      setQueenCoveredAt(Date.now());
      setQueenCoveredByMe(shooterIsMe);
    } else if (cls.sunkOwnCoins > 0 && shooterIsMe) {
      toast.push('success',
        `Sunk ${cls.sunkOwnCoins} coin${cls.sunkOwnCoins === 1 ? '' : 's'}`, 1400);
    }

    if (snap.gameOver) {
      // Defer the result popup — the winning shot still needs to
      // play through Board's local sim (if we're the shooter) or
      // the rAF replay (if the opponent is). The animation tail
      // calls `fireGameOverPopupIfPending`, which honours the
      // admin-set MATCH_END_RESULT_DELAY pause so the user sees
      // the last coin actually drop into the pocket before the
      // modal appears.
      const winnerSeat = snap.winnerSeat == null
        ? null
        : seatNumToKey(Number(snap.winnerSeat));
      gameOverPendingRef.current = { winnerSeat };
    }
  }

  // Called by the animation tails (handleShotResult after Board's
  // local sim, end-of-rAF after the opponent replay). If a gameOver
  // is queued, it fires the popup after `matchEndResultDelaySec`.
  function fireGameOverPopupIfPending() {
    const pending = gameOverPendingRef.current;
    if (!pending) return;
    gameOverPendingRef.current = null;
    const delayMs = Math.max(0, matchEndResultDelaySec * 1000);
    const won = pending.winnerSeat === yourSeatRef.current;
    matchTimers.current.push(window.setTimeout(() => {
      try { voiceRef.current?.stop(); } catch {}
      voiceRef.current = null;
      // Reveal sound — warm fanfare on a win, minor-third loss
      // tone on a loss. Plays IN SYNC with the result modal
      // appearing (admin-tunable delay above), so the audio cue
      // lands the moment the user reads "YOU WON" / "YOU LOST".
      if (won) playWinFanfare();
      else     playLossTone();
      setMatchOutcome(won ? 'win' : 'loss');
      setPhase('result');
    }, delayMs));
  }

  function applyServerShot(snap: any) {
    if (!snap) return;
    const myNum = seatKeyToNum(yourSeatRef.current);
    const shooterIsMe = Number(snap.seat) === myNum;

    // STEP 1 — apply game state IMMEDIATELY. The user's turn timer
    // starts counting against the server-side window the moment the
    // server says it's their turn; we mustn't gate that on a
    // 3-second visual playback.
    applyShotGameState(snap);

    if (shooterIsMe) {
      // Local shooter — Board.tsx's simCoins overlay is showing the
      // local sim. handleShotResult will apply finalCoins/finalStriker
      // when Board's animation ends. Nothing to do here visually.
      return;
    }

    // Opponent's shot — animate the server's timeline so the user
    // SEES the striker fly across the board, coins collide, and any
    // pocket drops play their sounds in sync.
    if (!Array.isArray(snap.timeline) || snap.timeline.length === 0) {
      // No timeline — teleport. Still fire the deferred game-over
      // popup so a server-side terminal state without a timeline
      // (rare) doesn't strand the user on the playing screen.
      if (Array.isArray(snap.finalCoins)) setCoins(snap.finalCoins);
      if (snap.finalStriker)              setStriker(snap.finalStriker);
      fireGameOverPopupIfPending();
      return;
    }

    // Cancel any previous in-flight replay AND snap to its tail
    // so the new replay starts from the correct opening frame
    // instead of mid-flight from the previous shot.
    if (replayRafRef.current != null) {
      cancelAnimationFrame(replayRafRef.current);
      replayRafRef.current = null;
    }
    setReplayActive(true);
    // Lock the striker colour to the SHOOTER's seat for the whole
    // duration of this remote rAF replay, so a yellow→blue or
    // blue→yellow flip can't happen mid-trajectory just because
    // applyShotGameState already advanced currentTurn to the next
    // shooter. Cleared in the rAF-tick's terminal branch below.
    setShootingSeat(seatNumToKey(Number(snap.seat)));

    const startedAt = performance.now();
    const STEP_MS = 1000 / 60;
    const events  = Array.isArray(snap.events) ? snap.events : [];
    let nextEventIdx = 0;
    const tick = () => {
      const elapsed = performance.now() - startedAt;
      const idx = Math.min(snap.timeline.length - 1, Math.floor(elapsed / STEP_MS));
      const f = snap.timeline[idx];
      if (f) {
        if (Array.isArray(f.coins))  setCoins(f.coins);
        if (f.striker)               setStriker(f.striker);
      }
      const elapsedSec = elapsed / 1000;
      while (nextEventIdx < events.length && events[nextEventIdx].t <= elapsedSec) {
        const ev = events[nextEventIdx];
        if (ev.type === 'collide-cc' || ev.type === 'collide-sc') {
          playCollision(ev.intensity ?? 0.6);
        } else if (ev.type === 'sink') {
          // Coin sink — pocket-drop sound. (The striker has its own
          // event type 'striker-sunk', handled below; this branch
          // never fires for the striker.)
          playPocketDrop();
        } else if (ev.type === 'striker-sunk') {
          // Striker pocketed mid-flight. Physics emits this distinct
          // event type (NOT a regular 'sink' with a flag — that was
          // the bug: applyShotGameState already shows the FOUL
          // overlay from `classified.foul`, but the in-flight buzzer
          // and the precise audio sync only happens here, so we play
          // the foul sound at the exact frame the puck disappears
          // into the pocket. Volume is the same buzzer the local
          // shooter hears in their own Board sim.
          playFoul();
        }
        nextEventIdx += 1;
      }
      if (idx >= snap.timeline.length - 1) {
        replayRafRef.current = null;
        setReplayActive(false);
        // Release the colour lock — the puck has finished animating,
        // it's resting on the next shooter's baseline now, and the
        // colour should follow `currentTurn` again.
        setShootingSeat(null);
        // Land precisely on the server's final coin / striker state
        // so we don't drift on rAF rounding.
        if (Array.isArray(snap.finalCoins)) setCoins(snap.finalCoins);
        if (snap.finalStriker)              setStriker(snap.finalStriker);
        // Animation done — if a gameOver was queued during this
        // shot, fire the result popup now (admin-tuned delay
        // included so the user gets a moment to see the last coin
        // drop into the pocket before the modal interrupts).
        fireGameOverPopupIfPending();
        return;
      }
      replayRafRef.current = requestAnimationFrame(tick);
    };
    replayRafRef.current = requestAnimationFrame(tick);
  }

  // Bound to <Board onShotResult>. Fires when our local replay
  // finishes. Game state has already been applied via
  // `applyShotGameState` the moment the server's snapshot arrived;
  // this just teleports the canonical final coins/striker into place
  // (Board's simCoins overlay just cleared, so no flicker).
  function handleShotResult(result: ShotResult) {
    const pending = pendingShotRef.current;
    pendingShotRef.current = null;
    // Local sim is over — release the striker colour lock so the
    // canonical respawn (which Board renders next) shows in the
    // colour matching whoever is on turn now (the opponent for
    // a pass-turn shot, me again for a retain-turn shot).
    setShootingSeat(null);
    if (pending) {
      if (Array.isArray(pending.finalCoins)) setCoins(pending.finalCoins);
      if (pending.finalStriker)              setStriker(pending.finalStriker);
      // Local sim's animation just ended. If a gameOver was queued
      // by `applyShotGameState`, fire the result popup now (with
      // the admin-set MATCH_END_RESULT_DELAY pause baked in).
      fireGameOverPopupIfPending();
      return;
    }
    // Server snapshot hadn't landed by the time the local sim ended
    // (rare — only on a high-RTT link). Fall back to the local
    // result; the next server frame will reconcile.
    //
    // Use the local user's seat baseline (y=0.86 for white seats
    // 1+3, y=0.14 for black seats 2+4) so the puck respawns on the
    // right side of the board for both teams. Hardcoding BASELINE_Y
    // (=0.86) made the seat-2 player's striker land on their
    // OPPONENT's row in this fallback path.
    setCoins(result.finalCoins);
    setStriker((prev) => {
      const seatNum = seatKeyToNum(yourSeatRef.current) as 1 | 2 | 3 | 4;
      const baseline = (seatNum === 1 || seatNum === 3) ? 0.86 : 0.14;
      let x = prev.x;
      if (!canPlaceStrikerAt(x, result.finalCoins, seatNum)) {
        for (let d = 0.002; d <= 0.5; d += 0.002) {
          if (canPlaceStrikerAt(x - d, result.finalCoins, seatNum)) { x = x - d; break; }
          if (canPlaceStrikerAt(x + d, result.finalCoins, seatNum)) { x = x + d; break; }
        }
      }
      return { x, y: baseline, sunk: false };
    });
    setTurnSeq((t) => t + 1);
  }

  // Throttle handle for the local slider-drag → server broadcast.
  // 50 ms = ~20 Hz, plenty smooth for the opponent's rendered
  // slide and well under any sane WS rate limit.
  const lastStrikerMoveSentAtRef = useRef(0);
  function handleStrikerMove(x: number) {
    setStriker((s) => ({ ...s, x }));
    // Broadcast the slide so the opponent's view of the puck moves
    // with the user's finger. Server validates that the sender
    // owns the current turn before relaying. Throttled to avoid
    // flooding (one frame per 50 ms is enough for visual smoothness
    // at the receiver's render cadence).
    if (wsRef.current) {
      const now = performance.now();
      if (now - lastStrikerMoveSentAtRef.current >= 50) {
        lastStrikerMoveSentAtRef.current = now;
        wsRef.current.send({ type: 'carrom_striker_move', x });
      }
    }
  }

  function handleExit() {
    if (phase === 'playing' || phase === 'vs' || phase === 'queue') {
      setQuitConfirm(true);
    } else {
      nav('/');
    }
  }

  function handleQuitConfirm() {
    clearMatchTimers();
    setQuitConfirm(false);
    // Tell the server. The kick path on the engine handles the
    // forfeit + finalize; we'll receive `carrom_left_game_success`
    // (which our handler already drops back to the lobby) and a
    // `carrom_match_over` for the surviving opponent.
    wsRef.current?.send({ type: 'carrom_leave_game' });
    setPhase('lobby');
    setMatchOutcome(null);
    toast.push('info', 'Match abandoned', 1600);
  }

  function handleNewMatch() {
    setMatchOutcome(null);
    setPhase('lobby');
  }

  // Dismiss the previous-match recovery card. Three things land in
  // sequence:
  //   1. localStorage gets the session id added so a refresh in
  //      the next 60 s never re-surfaces this card on this device
  //      — even if the server-side acknowledge POST blows up.
  //   2. POST /carrom/acknowledge-result tells the server to write
  //      `acknowledged_at` so a future device-fresh login also
  //      doesn't see it.
  //   3. On a successful POST we clear the localStorage entry —
  //      the server is now the source of truth and we don't want
  //      that bounded list growing forever.
  // Mirrors `acknowledgeRecovery` in games/ludo/Page.tsx.
  function acknowledgeRecovery() {
    if (!recovery) return;
    const sid = recovery.sessionId;
    markSessionAcked('carrom', sid);
    api.post('/carrom/acknowledge-result', { sessionId: sid })
      .then(() => clearSessionAcked('carrom', sid))
      .catch(() => { /* localStorage will keep suppressing it */ });
    setRecovery(null);
  }

  // Authoritative pot — comes straight from the server. Falls back
  // to `selectedBet * PLAYER_COUNT` only until carrom_match_start
  // /reconnect_sync lands, so the lobby still shows a sensible
  // preview before any session exists. Pre-fix this was always the
  // derived value, which went stale after a mid-match refresh or
  // when the lobby's `selectedBet` drifted from the locked match.
  const matchPot    = matchPotServer > 0 ? matchPotServer : selectedBet * PLAYER_COUNT;
  // Use the live admin-configured fee (loaded via /carrom/config on
  // mount); fall back to the constant only until the fetch resolves.
  const matchPayout = Math.floor(matchPot * (100 - platformFeePercent) / 100);

  const youName = useMemo(
    () => (user as { name?: string } | null)?.name?.toUpperCase() || 'YOU',
    [user],
  );

  // Local player's avatar URL from the auth context. Falls through
  // to the placeholder `IMG.player1` graphic only when the user has
  // no profile image set (or before /auth/me has resolved). The
  // VS-intro card AND the in-game profile card both consume this —
  // previously they each hardcoded `IMG.player1`, so the user never
  // saw their own face on either screen even though the avatar URL
  // was already on the auth user object. Same pattern Ludo uses
  // (`user?.profile_image_url || null`).
  const youAvatar = useMemo<string | null>(
    () => (user as { profile_image_url?: string | null } | null)?.profile_image_url || null,
    [user],
  );

  return (
    <div className="ludo-root carrom-root fixed inset-0 z-[9999]">
      {/* Loading overlay — covers the route until WS auth AND every
          preloaded asset URL (Logo, Board.png, etc.) have settled. */}
      <GameLoadingScreen
        visible={!authReady || !assetStatus.ready}
        theme="carrom"
        progress={assetStatus.progress}
      />
      <div className="app-container shadow-2xl">
        <GameSettingsMenu
          open={settingsOpen}
          soundOn={soundEnabled}
          micMuted={micMuted}
          listenMuted={listenMuted}
          voiceEnabled={phase === 'playing' || phase === 'vs'}
          onToggleSound={() => { playTap(); setSoundEnabled((s) => !s); }}
          onToggleMic={() => {
            playTap();
            setMicMuted((muted) => {
              const next = !muted;
              voiceRef.current?.setMuted(next);
              // Mic-unmute path — if start() was previously rejected
              // (permission denied) we won't have a live capture
              // pipeline. Try to re-acquire under THIS click gesture
              // (cookbook Rule 4 fallback path) so the next press is
              // not a no-op.
              if (!next && voiceRef.current && !voiceRef.current.hasLocalStream()) {
                navigator.mediaDevices?.getUserMedia({ audio: true, video: false })
                  .then((stream) => voiceRef.current?.attachLocalStream(stream))
                  .catch((e) => {
                    console.warn('[carrom/voice] mic re-acquire failed', e);
                    toast.push('error', 'Mic blocked — check browser permissions', 2200);
                  });
              }
              return next;
            });
          }}
          onToggleListen={() => {
            playTap();
            setListenMuted((muted) => {
              const next = !muted;
              voiceRef.current?.setListenMuted(next);
              // Cookbook Rule 7 — Safari can leave the playback context
              // suspended after a mute toggle; nudge it back to life on
              // listen-unmute so remote frames resume immediately.
              if (!next) voiceRef.current?.kickAudioPlayback();
              return next;
            });
          }}
          onExit={() => { playTap(); setSettingsOpen(false); handleExit(); }}
          onClose={() => setSettingsOpen(false)}
          exitLabel={(phase === 'playing' || phase === 'vs') ? 'Exit Game' : 'Exit to Home'}
          anchor="left"
        />

        <CarromTopBar
          walletAmt={wallet}
          potPayout={phase === 'playing' ? matchPayout : null}
          cashBannerImg={
            phase === 'playing'
              ? IMG.cash[Math.max(0, Math.min(IMG.cash.length - 1, BET_TIERS_FALLBACK.indexOf(selectedBet)))]
              : null
          }
          onBack={() => { playTap(); handleExit(); }}
          onSettings={() => { playTap(); setSettingsOpen((o) => !o); }}
          onRecharge={() => { playTap(); nav('/wallet'); }}
        />

        {phase === 'lobby' && (
          <CarromLobby
            betTiers={betTiers}
            platformFeePercent={platformFeePercent}
            onPick={(bet) => { playTap(); startMatch(bet); }}
            online={onlinePlayers}
            waitingByBet={waitingByBet}
            showWaitingBadge={showWaitingBadge}
          />
        )}

        {phase === 'queue' && (
          <CarromWaitingModal
            onCancel={() => { playTap(); cancelQueue(); }}
            yourAvatar={youAvatar}
            yourName={youName}
            profiles={queueProfiles.profiles}
            yourUserId={queueProfiles.yourUserId}
          />
        )}

        {phase === 'vs' && (
          <CarromVsScreen
            yourName={youName}
            yourAvatar={youAvatar}
            opponentName={oppName}
            opponentAvatar={oppAvatar}
            pot={matchPot}
            payout={matchPayout}
          />
        )}

        {phase === 'playing' && (
          <CarromGameScreen
            coins={coins}
            striker={striker}
            yourSeat={yourSeat}
            currentTurn={currentTurn}
            onShotResult={handleShotResult}
            onShotFired={(shot) => {
              // Lock the striker colour to MY seat for the whole
              // duration of the local Board.tsx sim. Without this
              // the Board's onShotResult lands AFTER the server's
              // shot_resolved (because the local sim is roughly the
              // same length as the server timeline), and the colour
              // would already have flipped to the opponent the
              // instant the server reply moved currentTurn — that's
              // the mid-flight blue flash the user reported.
              setShootingSeat(yourSeat);
              wsRef.current?.send({
                type:     'carrom_shoot',
                angle:    shot.angle,
                power:    shot.power,
                strikerX: shot.strikerX,
              });
            }}
            onAimUpdate={(aim) => {
              if (!wsRef.current) return;
              if (aim === null) {
                wsRef.current.send({ type: 'carrom_aim_clear' });
              } else {
                wsRef.current.send({
                  type:     'carrom_aim_update',
                  angle:    aim.angle,
                  power:    aim.power,
                  strikerX: aim.strikerX,
                });
              }
            }}
            onStrikerMove={handleStrikerMove}
            score={score}
            fouls={fouls}
            queenWith={queenWith}
            turnSeq={turnSeq}
            youName={youName}
            youAvatar={youAvatar}
            oppName={oppName}
            oppAvatar={oppAvatar}
            oppBotMode={oppBotMode}
            micMuted={micMuted}
            listenMuted={listenMuted}
            foulShownAt={foulShownAt}
            foulReason={foulReason}
            foulIsMine={foulIsMine}
            foulCategory={foulCategory}
            queenCoveredAt={queenCoveredAt}
            queenCoveredByMe={queenCoveredByMe}
            // Black-team seats (P2 / P4) view the board rotated
            // 180° so their own striker visually sits at the bottom.
            viewFlip={yourSeat === 'p2' || yourSeat === 'p4'}
            opponentAim={opponentAim}
            replayActive={replayActive}
            turnSeconds={turnSeconds}
            shootingSeat={shootingSeat}
          />
        )}

        {phase === 'result' && matchOutcome && (
          <CarromResultScreen
            outcome={matchOutcome}
            payout={matchPayout}
            pot={matchPot}
            onHome={() => { playTap(); nav('/'); }}
            onNewMatch={() => { playTap(); handleNewMatch(); }}
          />
        )}

        {quitConfirm && (
          <CarromQuitConfirm
            amount={selectedBet}
            onStay={() => { playTap(); setQuitConfirm(false); }}
            onQuit={() => { playTap(); handleQuitConfirm(); }}
          />
        )}

        <InsufficientFundsModal
          open={insufficientOpen}
          required={insufficientBet}
          balance={wallet}
          onClose={() => setInsufficientOpen(false)}
        />

        {/* Concurrent-match modal — fires when the server rejects this
            game's join_queue with err='concurrent_match_limit' because
            the player is already in another live game. Shows which
            game is blocking + how much they'd forfeit + Quit/Continue
            buttons. Same shared component the other 4 games use. */}
        <ConcurrentMatchModal
          open={concOpen}
          blocking={concBlocking}
          walletBalance={wallet}
          onResolved={() => {
            setConcOpen(false);
            setConcBlocking(null);
            toast.push('success', 'Active matches resolved — try again', 2000);
          }}
          onCancel={() => setConcOpen(false)}
        />

        {/* ========== RECOVERY (loaded from /pending-result) ==========
            Only shown on the lobby — `phase` gates the render so a
            mid-match reconnect (which arrives via carrom_reconnect_sync
            and flips us straight to 'playing') doesn't briefly flash
            this card behind the live board. */}
        {recovery && phase === 'lobby' && (
          <CarromRecoveryScreen
            kind={
              recovery.result === 'win'  ? 'win'
              : recovery.result === 'kick' ? 'kick'
              : recovery.result === 'draw' ? 'draw'
              : 'loss'
            }
            amount={recovery.result === 'win'
              ? recovery.payout
              : (recovery.betAmount || recovery.pot || 0)}
            platformFee={recovery.platformFee}
            yourCoins={recovery.yourCoins}
            oppCoins={recovery.oppCoins}
            queenWith={recovery.queenWith}
            opponents={recovery.opponents}
            onAck={() => { playTap(); acknowledgeRecovery(); }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Top bar ─────────────────────────────────────────────────────────
// Top bar — Ludo-style structure (back button + image-style settings
// button on the left, centre pot/payout chip during active play, and
// wallet chip with "+" affordance on the right). Same visual rhythm
// as Ludo so the platform feels consistent across games. Only the
// gradient + accent colour change to fit the dark-blue Carrom theme.
function CarromTopBar({
  walletAmt, potPayout, cashBannerImg, onBack, onSettings, onRecharge,
}: {
  walletAmt: number;
  potPayout: number | null;
  cashBannerImg: string | null;
  onBack: () => void;
  onSettings: () => void;
  /** Tap on the green "+" inside the wallet chip — navigates to
   *  `/wallet` for top-up. Mirrors what every game's wallet chip
   *  should do (Ludo's button is purely cosmetic today; this one
   *  actually wires it). */
  onRecharge: () => void;
}) {
  return (
    <div
      className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#020a26] to-[#051640] shadow-md z-[150] w-full"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
    >
      {/* Left cluster — back + settings, with no horizontal split. */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onBack}
          aria-label="Back"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
        >
          <Icon name="arrow_back" size={22} filled />
        </button>
        <button
          onClick={onSettings}
          aria-label="Settings"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
        >
          <img src={IMG.settings} className="w-full h-full object-contain" alt="Settings" />
        </button>
      </div>

      {/* Centre pot chip — only visible during active play. Same shape
          as the wallet chip on the right so the bar reads symmetrical. */}
      {potPayout != null && cashBannerImg && (
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div
            className="bg-gradient-to-b from-[#0a1a47] to-[#102b6e] rounded-lg flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-cyan-400/30 shadow-[0_0_30px_rgba(34,211,238,0.10)] relative"
            title="Winner take-home (full pot)"
          >
            <img
              src={cashBannerImg}
              className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10"
              alt=""
            />
            <div className="flex flex-col items-start leading-none mr-1 sm:mr-2">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.2em] text-white/70">
                WIN
              </span>
              <span className="text-white font-bold text-base sm:text-lg tracking-wide tabular-nums mt-0.5">
                {fmt(potPayout)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Wallet chip — coin image, balance, and "+" recharge affordance. */}
      <div className="bg-gradient-to-b from-[#0a1a47] to-[#102b6e] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-cyan-400/30 shadow-[0_0_30px_rgba(34,211,238,0.10)] relative">
        <img src={IMG.coin} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
        <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide">
          {fmt(walletAmt)}
        </span>
        <button
          type="button"
          aria-label="Recharge wallet"
          onClick={onRecharge}
          className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        >
          <img src={IMG.plus} className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
        </button>
      </div>
    </div>
  );
}

// ─── Lobby ───────────────────────────────────────────────────────────
// MVP is 2-player only — the 2P/4P chooser tile that used to live
// here has been removed. 4P is deferred (see CARROM_BUILD_PLAN.md
// §13). When 4P ships, the chooser tile and `playerCount` plumbing
// come back; until then `winAmt` is computed against a fixed 2-seat
// pot so it's clear the prize is `bet × 2 × (1 − fee%)`.
function CarromLobby({
  betTiers, platformFeePercent, onPick, online,
  waitingByBet, showWaitingBadge,
}: {
  betTiers: number[]; platformFeePercent: number;
  onPick: (bet: number) => void; online: number;
  /** Per-stake "real humans queued" counts, keyed by bet amount as
   *  string (matches the WS payload shape). Empty object = nobody
   *  queued anywhere. */
  waitingByBet: Record<string, number>;
  /** Server-side admin gate (CarromGame.SHOW_QUEUE_WAITING_BADGE).
   *  When false, badges are hidden even if humans are queued. */
  showWaitingBadge: boolean;
}) {
  const [selectedBet, setSelectedBet] = useState<number>(betTiers[0] ?? 0);
  return (
    <div
      className="flex flex-col w-full h-full flex-grow rounded-none sm:rounded-b-3xl relative"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex flex-col flex-grow w-full">
        <div className="flex-grow flex flex-col justify-center items-center px-4 sm:px-6 min-h-[150px]">
          <img
            src={IMG.logo}
            alt=""
            className="w-full h-full object-contain drop-shadow-2xl"
            style={{ maxHeight: 240 }}
          />
          <div className="carrom-online-pill mt-4">
            <span className="carrom-online-dot" aria-hidden />
            <span>{fmt(online)} PLAYERS ONLINE</span>
          </div>
        </div>

        <div className="px-4 sm:px-5 mt-auto flex flex-col gap-3 w-full pb-6">
          <div className="carrom-bestof-chip">
            <span>Match Format</span>
            <span className="pill">Best of 1</span>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {betTiers.map((val, i) => {
              const winAmt = Math.floor(val * 2 * (100 - platformFeePercent) / 100);
              const isSelected = selectedBet === val;
              const waiting = Number(waitingByBet[String(val)] || 0) | 0;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => { setSelectedBet(val); onPick(val); }}
                  className={'carrom-cash-btn' + (isSelected ? ' selected' : '')}
                  style={{ position: 'relative' }}
                >
                  <div className="flex flex-col items-start justify-center pointer-events-none">
                    <span className={'carrom-cash-amt' + (val === 0 ? ' carrom-cash-amt--free' : '')}>
                      {val === 0 ? 'FREE' : val}
                    </span>
                    <span className="carrom-cash-win">
                      {val === 0 ? 'PLAY FOR FUN' : `WIN ${winAmt}`}
                    </span>
                  </div>
                  <img
                    src={IMG.cash[i] || IMG.cash[IMG.cash.length - 1]}
                    className="carrom-cash-img pointer-events-none"
                    alt=""
                  />
                  {showWaitingBadge && waiting > 0 && (
                    <CarromWaitingBadge count={waiting} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── "N waiting" cash-tile badge ─────────────────────────────────
// Small pill on the top-right of a cash tile when real humans are
// queued at that stake. Clones Ludo's `WaitingBadge` exactly —
// reuses the `.cash-waiting-badge` CSS already imported via
// `../ludo/ludo.css` so the visual is identical between the two
// lobbies. Bots are NEVER counted in `count`; the server filters
// them out so the number is an honest signal of human activity.
function CarromWaitingBadge({ count }: { count: number }) {
  return (
    <span
      className="cash-waiting-badge"
      aria-label={`${count} waiting`}
      title={`${count} waiting at this stake`}
    >
      <svg
        width="10" height="10" viewBox="0 0 24 24"
        fill="currentColor" aria-hidden="true"
        className="cash-waiting-badge__icon"
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
      <span className="cash-waiting-badge__num">{count}</span>
      <span className="cash-waiting-badge__label">waiting</span>
    </span>
  );
}

// ─── Waiting modal ───────────────────────────────────────────────────
function CarromWaitingModal({
  onCancel, yourAvatar, yourName, profiles, yourUserId,
}: {
  onCancel: () => void;
  yourAvatar: string | null;
  yourName: string;
  profiles: Array<{ userId?: string; name?: string; displayName?: string; avatarUrl?: string | null; isBot?: boolean; is_bot?: boolean }>;
  yourUserId: string | null;
}) {
  const [dots, setDots] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d + 1) % 3), 400);
    return () => clearInterval(t);
  }, []);
  const dotsText = dots === 0 ? '.' : dots === 1 ? '..' : '...';
  // Resolve the opponent slot from the live queue snapshot. As soon
  // as carrom_waiting_in_queue lists a profile that ISN'T the local
  // player, swap the dashed-border search-pulse for the real avatar
  // + name. Until then the placeholder remains.
  const opponent = (profiles || []).find((p) => p && String(p.userId || '') !== String(yourUserId || ''));
  const oppName = opponent ? (opponent.name || opponent.displayName || 'Opponent') : null;
  const oppAvatar = opponent?.avatarUrl || null;
  return (
    <div className="absolute inset-0 z-[160] bg-black/85 flex justify-center items-center">
      <div className="relative w-[85%] max-w-[340px] border-[3px] border-[#d4af37] bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#0a1a47] via-[#051640] to-[#020617] rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.9)] flex flex-col items-center gap-4">
        <button
          onClick={onCancel}
          aria-label="Cancel"
          className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform z-10"
        >
          ✕
        </button>
        <img src={IMG.logo} className="w-24 h-auto object-contain drop-shadow-2xl mb-1" alt="" />
        <h2 className="text-center">
          <span className="block text-xs tracking-[0.3em] text-cyan-200 uppercase">Finding</span>
          <span className="block text-5xl font-black leading-none my-1 text-cyan-300 drop-shadow-[0_0_14px_rgba(34,211,238,0.45)]">
            Opponent
          </span>
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-16 h-16 rounded-full overflow-hidden border-[3px] border-cyan-400 shadow-[0_0_14px_rgba(34,211,238,0.65)]">
              {/* Use the auth-context profile image when available so
                  the user sees their OWN face in the waiting splash —
                  matches the VS screen and in-game profile card.
                  Falls back to the placeholder PNG only when the user
                  has no profile image set or the auth context is
                  still loading. */}
              <img src={yourAvatar || IMG.player1} className="w-full h-full object-cover" alt="" />
            </div>
            <div className="text-[10px] font-black tracking-widest text-white truncate max-w-[78px] text-center">
              {(yourName || 'YOU').toUpperCase()}
            </div>
          </div>
          <div className="carrom-wait-vs">V/S</div>
          <div className="flex flex-col items-center gap-1.5" style={{ width: 78 }}>
            {opponent ? (
              <>
                <div className="w-16 h-16 rounded-full overflow-hidden border-[3px] border-rose-300 shadow-[0_0_14px_rgba(251,113,133,0.55)]">
                  <img src={oppAvatar || IMG.player2} className="w-full h-full object-cover" alt="" />
                </div>
                <div className="text-[10px] font-black tracking-widest text-rose-200 truncate max-w-full text-center">
                  {(oppName || 'OPPONENT').toUpperCase()}
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full overflow-hidden border-[3px] border-dashed border-rose-300 bg-black/30 relative">
                  <img src={IMG.player2} className="w-full h-full object-cover opacity-90" alt="" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Icon name="search" size={26} filled className="text-white/95 animate-pulse" />
                  </div>
                </div>
                <div className="text-[10px] font-black tracking-widest text-rose-200">{dotsText}</div>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onCancel}
          className="w-full py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#e63946] to-[#900c16] text-white font-black tracking-widest shadow-md hover:brightness-110 active:scale-[0.98] transition-all"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

// ─── VS screen ───────────────────────────────────────────────────────
// MVP is 2-player only. The optional partner-row that used to render
// when `playerCount === 4` is gone. Kept the YOU vs OPPONENT layout
// otherwise unchanged.
//
// Both avatar slots and the opponent name now come in as live props.
// Earlier the component hardcoded `IMG.player1` / `IMG.player2` and
// the literal string "OPPONENT", so the user never saw their actual
// face or the matched player's name even though the WS
// `match_start` frame already carried both. Falling back to the
// shipped placeholder graphics keeps the splash usable when an
// avatar URL is missing (new accounts, or the brief moment before
// the auth context resolves).
function CarromVsScreen({
  yourName, yourAvatar,
  opponentName, opponentAvatar,
  pot, payout,
}: {
  yourName: string;
  yourAvatar: string | null;
  opponentName: string;
  opponentAvatar: string | null;
  pot: number;
  payout: number;
}) {
  return (
    <div className="absolute inset-0 z-[180] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#0a1a47] to-[#020617] flex flex-col items-center justify-center gap-6 p-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{
             background: 'radial-gradient(circle at 30% 30%, rgba(34,211,238,0.25), transparent 40%), radial-gradient(circle at 70% 70%, rgba(99,102,241,0.25), transparent 40%)',
           }} />
      <div className="text-[10px] uppercase tracking-[0.4em] text-cyan-200">MATCH FOUND</div>
      <div className="flex items-center gap-5">
        <VsCard name={yourName} img={yourAvatar || IMG.player1} ringColor="#22d3ee" isMe />
        <div className="text-4xl font-black text-yellow-300 drop-shadow">V / S</div>
        <VsCard name={opponentName || 'OPPONENT'} img={opponentAvatar || IMG.player2} ringColor="#fb7185" />
      </div>
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-200">POT</div>
        <div className="text-2xl font-black text-yellow-300">{fmt(pot)}</div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-200 mt-1">WIN {fmt(payout)}</div>
      </div>
    </div>
  );
}

function VsCard({ name, img, ringColor, isMe, small }: {
  name: string; img: string; ringColor: string; isMe?: boolean; small?: boolean;
}) {
  const size = small ? 'w-14 h-14' : 'w-20 h-20';
  return (
    <div className={`flex flex-col items-center gap-2 ${isMe ? 'scale-110' : ''}`}>
      {isMe && (
        <div className="bg-yellow-300 text-black px-2 py-0.5 rounded text-[9px] font-black tracking-widest">YOU</div>
      )}
      <div
        className={`${size} rounded-full overflow-hidden`}
        style={{ border: `3px solid ${ringColor}`, boxShadow: `0 0 18px ${ringColor}80` }}
      >
        <img src={img} className="w-full h-full object-cover" alt="" />
      </div>
      <div className="text-xs font-black tracking-widest uppercase text-white">{name}</div>
    </div>
  );
}

// ─── Game screen ─────────────────────────────────────────────────────
// Game screen — Ludo-style two-profile layout. Opponent profile sits
// above the board, local profile below. The round/score/queen/fouls
// info ribbons that used to sit between the top bar and the
// opponent's row are GONE — every piece of relevant state has moved
// into the profile cards themselves (kill counter, queen badge,
// foul dots, turn-glow). This is the structural change the user
// asked for: zero meta-text, all status read directly off the
// profile chrome.
function CarromGameScreen({
  coins, striker, yourSeat, currentTurn,
  onShotResult, onShotFired, onAimUpdate, onStrikerMove,
  score, fouls, queenWith, turnSeq, youName, youAvatar,
  oppName, oppAvatar, oppBotMode,
  micMuted, listenMuted, foulShownAt, foulReason, foulIsMine, foulCategory,
  queenCoveredAt, queenCoveredByMe,
  viewFlip, opponentAim, replayActive, turnSeconds,
  shootingSeat,
}: {
  coins: Coin[];
  striker: Striker;
  yourSeat: Seat;
  currentTurn: Seat;
  onShotResult: (r: ShotResult) => void;
  onShotFired?: (shot: { angle: number; power: number; strikerX: number }) => void;
  onAimUpdate?: (aim: { angle: number; power: number; strikerX: number } | null) => void;
  onStrikerMove: (x: number) => void;
  score: { you: number; opp: number };
  fouls: { you: number; opp: number };
  queenWith: 'you' | 'opp' | null;
  turnSeq: number;
  youName: string;
  /** Local user's avatar URL from the auth context. Falls through
   *  to `IMG.player1` only when null (new account / pre-auth). */
  youAvatar: string | null;
  oppName: string;
  oppAvatar: string | null;
  /** When the opponent is a bot, their difficulty mode
   *  ('easy' | 'medium' | 'hard'). null for human opponents. */
  oppBotMode: string | null;
  micMuted: boolean;
  listenMuted: boolean;
  foulShownAt: number | null;
  /** Human-readable foul subtitle ("striker pocketed", "you sunk
   *  opponent's coin", etc.). Rendered inside the FOUL overlay; null
   *  hides the subtitle. Sourced from the parent's foul state. */
  foulReason: string | null;
  /** True when YOU are the player who fouled (so the subtitle gets
   *  a red-tinged warning accent); false when the OPPONENT fouled
   *  (amber-tinged informational accent so you know what happened). */
  foulIsMine: boolean;
  /** Foul VISUAL category — distinct look per category so the player
   *  can tell apart "striker mishandled" (dramatic red) from
   *  "wrong coin pocketed" (amber warning). Drives both the overlay
   *  glyph and the colour theme. */
  foulCategory: 'striker' | 'coin' | 'no_contact' | null;
  /** Phase 24 — celebratory queen-cover banner. Time-tagged so a
   *  repeat cover (rare across very long matches) re-triggers the
   *  pop animation cleanly via the React key. */
  queenCoveredAt: number | null;
  queenCoveredByMe: boolean;
  viewFlip: boolean;
  opponentAim: { angle: number; power: number; strikerX?: number | null } | null;
  replayActive: boolean;
  /** Live `CarromGame.TURN_SECONDS`. Forwarded into both profile
   *  cards so the per-turn countdown bar uses the current admin
   *  setting (no rebuild needed when admin retunes turn length). */
  turnSeconds: number;
  /** Seat of the player whose striker is currently in flight (local
   *  sim or remote rAF replay). null when no shot is animating. */
  shootingSeat: Seat | null;
}) {
  const isYourTurn = currentTurn === yourSeat;
  // Striker colour rule: while a shot is animating the striker keeps
  // the SHOOTER's colour (yellow if I shot, blue if opponent shot)
  // — the moment the animation ends, colour follows currentTurn.
  // This eliminates the mid-flight blue flash the user reported
  // when the server's shot_resolved arrives BEFORE the local sim
  // ends and currentTurn flips to the next shooter while the puck
  // is still flying.
  const strikerIsOpponent = shootingSeat != null
    ? shootingSeat !== yourSeat
    : !isYourTurn;
  // Carrom team mapping: P1 + P3 are the WHITE team, P2 + P4 are
  // BLACK. Each player should see their team's coin colour on the
  // kill counter and their team's accent on the active-turn ring.
  const myTeam:  'white' | 'black' = (yourSeat === 'p1' || yourSeat === 'p3') ? 'white' : 'black';
  const oppTeam: 'white' | 'black' = myTeam === 'white' ? 'black' : 'white';
  // Shine my own coins ONLY when it's my turn (cookbook UX rule —
  // a player's own coins pulse as a target hint while it's their
  // shot, but never glow on the opponent's turn).
  const shineColor: 'white' | 'black' | null = isYourTurn ? myTeam : null;

  return (
    <div className="carrom-game-wrap relative">
      {/* Opponent profile — rendered above the board so they sit
          "across the table". Card chrome carries THEIR team colour
          (the carrom-coin colour they're sinking) so each player
          can read at a glance who's playing what. */}
      <CarromPlayerCard
        position="top"
        isMe={false}
        isTurn={!isYourTurn}
        name={oppName || 'OPPONENT'}
        team={oppTeam}
        kills={score.opp}
        queenHere={queenWith === 'opp'}
        fouls={fouls.opp}
        turnSeq={turnSeq}
        avatarSrc={oppAvatar || IMG.player2}
        botMode={oppBotMode}
        turnSeconds={turnSeconds}
        // Phase 24 — freeze the per-turn countdown while the FOUL
        // overlay is on screen. The user explicitly asked for this:
        // neither player should lose seconds while reading the foul
        // message. Both player cards share the same paused flag so
        // either card's visible timer halts.
        timerPaused={foulShownAt != null || queenCoveredAt != null}
      />

      <Board
        coins={coins}
        striker={striker}
        yourSeat={yourSeat}
        canShoot={isYourTurn}
        onShotResult={onShotResult}
        onShotFired={onShotFired}
        onAimUpdate={onAimUpdate}
        onStrikerMove={onStrikerMove}
        shineColor={shineColor}
        viewFlip={viewFlip}
        opponentAim={opponentAim}
        replayActive={replayActive}
        strikerIsOpponent={strikerIsOpponent}
      />

      {/* Local profile — your team's accent + your team's coin in
          the kill pill. Uses the auth-context profile_image_url so
          the player sees their own face on the card; falls back to
          IMG.player1 only when the user has no profile image set
          (new accounts or the brief pre-/auth/me window). */}
      <CarromPlayerCard
        position="bottom"
        isMe={true}
        isTurn={isYourTurn}
        name={youName}
        team={myTeam}
        kills={score.you}
        queenHere={queenWith === 'you'}
        fouls={fouls.you}
        turnSeq={turnSeq}
        avatarSrc={youAvatar || IMG.player1}
        micMuted={micMuted}
        listenMuted={listenMuted}
        turnSeconds={turnSeconds}
        timerPaused={foulShownAt != null || queenCoveredAt != null}
      />

      {/* Foul overlay — big centred FOUL message + a human-readable
          subtitle saying which rule was broken (striker pocketed /
          no contact / opponent's coin sunk). 1.5s auto-dismiss; the
          React `key` (foulShownAt) makes consecutive fouls re-trigger
          the animation cleanly. The subtitle gets a who-fouled colour
          accent — red-tinged when YOU fouled (so it reads as a
          warning), amber-tinged when the opponent fouled (so it
          reads as informational, since you're not the one being
          penalised). */}
      {/* Phase 24 — queen-cover celebration banner. Same overlay layer
          as the foul, mutually exclusive (the server only fires
          queenJustCovered on CLEAN cover shots, so a foul can never
          co-occur). Always shown to BOTH players — the covering
          side sees "+3" celebratory styling, the other side sees
          "OPPONENT COVERED THE QUEEN" so they know what just changed. */}
      {queenCoveredAt != null && (
        <div
          className="carrom-queen-banner"
          key={queenCoveredAt}
          data-mine={queenCoveredByMe ? '1' : '0'}
          aria-hidden
        >
          <span className="carrom-queen-banner-glyph">♛</span>
          <span className="carrom-queen-banner-text">
            {queenCoveredByMe ? 'QUEEN COVERED!' : 'OPPONENT COVERED THE QUEEN'}
          </span>
          {queenCoveredByMe && (
            <span className="carrom-queen-banner-bonus">+3 BONUS</span>
          )}
        </div>
      )}
      {foulShownAt != null && (
        <div
          className="carrom-foul-overlay"
          key={foulShownAt}
          data-cat={foulCategory || 'striker'}
          data-mine={foulIsMine ? '1' : '0'}
          aria-hidden
        >
          {/* Glyph that VISUALLY differentiates the foul category.
              striker / no_contact → striker glyph (the cue going wrong);
              coin → coin glyph (wrong target sunk). */}
          <span className="carrom-foul-overlay-glyph">
            {foulCategory === 'coin' ? '⬤' : '◎'}
          </span>
          <span className="carrom-foul-overlay-text">FOUL!</span>
          {foulReason && (
            <span
              className="carrom-foul-overlay-subtitle"
              data-mine={foulIsMine ? '1' : '0'}
            >
              {foulReason}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Profile card (one player, Ludo-equivalent layout) ──────────────
//
// Avatar + foul-dot strip in the left column, name + YOU/team meta
// in the middle, kill counter + queen badge on the right. A 16-second
// countdown timer bar attaches above the card for top players and
// below the card for bottom players (matching Ludo's geometry so the
// active turn always reads with the bar visually adjacent to the
// board edge it touches).
function CarromPlayerCard({
  position, isMe, isTurn, name, team, kills, queenHere, fouls, turnSeq,
  avatarSrc, micMuted, listenMuted, botMode, turnSeconds, timerPaused,
}: {
  position: 'top' | 'bottom';
  isMe: boolean;
  isTurn: boolean;
  name: string;
  /** Which carrom team this card represents — drives the accent
   *  colour (white = warm gold, black = deep crimson), the kill-pill
   *  coin colour, and the META line ("WHITE" / "BLACK"). */
  team: 'white' | 'black';
  kills: number;
  queenHere: boolean;
  fouls: number;
  turnSeq: number;
  avatarSrc: string;
  micMuted?: boolean;
  listenMuted?: boolean;
  /** When this card represents a bot opponent, the bot's difficulty
   *  ('easy' | 'medium' | 'hard'). Renders a small tag next to the
   *  team name so testers can confirm the AI tier they're playing
   *  against. Omitted for human opponents and the local player. */
  botMode?: string | null;
  /** Live `CarromGame.TURN_SECONDS`. Drives the per-turn timer
   *  bar's animation duration; admin can retune turn length and
   *  this prop carries the new value through without a rebuild. */
  turnSeconds: number;
  /** When true, the per-turn countdown is FROZEN (e.g. while a FOUL
   *  overlay is being shown so the active player doesn't lose seconds
   *  reading the message). Released the moment this flips back to
   *  false. Both player cards receive the same flag. */
  timerPaused?: boolean;
}) {
  const isTop = position === 'top';
  // Accent colours map to the team's carrom-coin look. White team =
  // warm gold (matches the cream coin); black team = deep crimson.
  const teamColor = team === 'white' ? '#fde68a' : '#dc2626';
  const teamGlow  = team === 'white'
    ? 'rgba(253, 230, 138, 0.55)'
    : 'rgba(220, 38, 38, 0.55)';
  return (
    <div className="w-full flex flex-col items-center">
      {/* Timer bar — visible only on the active player's card. Above
          for top player, below for bottom. Restarts on every turn
          via the React key. */}
      <div
        className={`carrom-turn-bar ${isTop ? 'order-last' : 'order-first'}`}
        style={{ visibility: isTurn ? 'visible' : 'hidden' }}
      >
        {isTurn
          ? <CarromTurnBarFill key={`${team}-${turnSeq}`} durationSec={turnSeconds} paused={!!timerPaused} />
          : <div className="carrom-turn-bar-fill" />}
      </div>

      <div
        className="carrom-pcard"
        style={{
          borderColor: isTurn ? teamColor : '#bc9648',
          boxShadow: isTurn
            ? `0 0 14px 2px ${teamGlow}, 0 5px 15px rgba(0,0,0,0.5)`
            : '0 5px 15px rgba(0,0,0,0.5)',
        }}
      >
        {/* Avatar + foul-dot strip below it */}
        <div className="flex flex-col items-center w-[55px] shrink-0">
          <div
            className="carrom-pcard-avatar"
            style={{ borderColor: teamColor }}
          >
            <img src={avatarSrc} alt="" />
          </div>
          <div className="carrom-foul-dots mt-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`carrom-foul-dot ${i < fouls ? 'on' : ''}`} />
            ))}
          </div>
        </div>

        {/* Middle column — name + meta line */}
        <div className="flex flex-col justify-center flex-grow pl-1.5 min-w-0 overflow-hidden z-10 min-h-[50px]">
          <span className="text-[12px] text-white font-bold leading-tight truncate uppercase">
            {name}
          </span>
          {isMe ? (
            <span className="mt-1 flex items-center gap-1 leading-none w-max">
              <span className="bg-white text-black px-1.5 py-0.5 rounded text-[8px] font-black animate-pulse">
                YOU
              </span>
              {!micMuted && (
                <Icon name="mic" size={12} filled className="text-white/90 drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]" />
              )}
              {!listenMuted && (
                <Icon name="headphones" size={12} filled className="text-white/90 drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]" />
              )}
            </span>
          ) : (
            <span className="text-[10px] text-cyan-200/70 font-semibold mt-1 tracking-widest uppercase flex items-center gap-1.5">
              <span>{team === 'white' ? 'WHITE' : 'BLACK'}</span>
              {/* Bot difficulty pill removed — players must never see
                  any indicator that they're playing against a bot.
                  The `botMode` prop is still threaded through for
                  internal use / future test surfaces but renders
                  nothing. See PR notes for the platform-wide rule. */}
            </span>
          )}
        </div>

        {/* Right column — own-coin kill counter + queen badge.
            Shows the player's team coin (white or black) plus the
            number of their own coins they've sunk. The queen badge
            only appears once the queen is COVERED by this player. */}
        <div className="flex flex-col items-end gap-1.5 ml-auto shrink-0 pr-1">
          <div className="carrom-kill-pill">
            <div className={`carrom-kill-coin ${team}`} />
            <span className="carrom-kill-count">{kills}</span>
          </div>
          {queenHere && <div className="carrom-kill-queen" aria-label="Queen secured" />}
        </div>
      </div>
    </div>
  );
}

// Countdown bar that mirrors the server-side TURN_SECONDS exactly.
// Full cyan at start, transitions linearly to 0 % width + rose fill
// over the full turn window. The parent re-mounts this via
// `key={turnSeq}` so the animation restarts cleanly each turn.
// `durationSec` is the live CarromGame.TURN_SECONDS — admin can
// retune turn length without rebuilding the client.
function CarromTurnBarFill({ durationSec, paused }: { durationSec: number; paused?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Switched from CSS transition to CSS animation so we can PAUSE
    // it via animation-play-state when a FOUL overlay is on screen
    // (transitions can't be paused mid-flight without snapshot+restart).
    // The keyframes live in carrom.css → @keyframes carrom-turn-drain.
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = `carrom-turn-drain ${durationSec}s linear forwards`;
  }, [durationSec]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.animationPlayState = paused ? 'paused' : 'running';
  }, [paused]);
  return <div ref={ref} className="carrom-turn-bar-fill" />;
}

// ─── Result screen ───────────────────────────────────────────────────
function CarromResultScreen({
  outcome, payout, pot, onHome, onNewMatch,
}: {
  outcome: 'win' | 'loss';
  payout: number; pot: number;
  onHome: () => void; onNewMatch: () => void;
}) {
  const title = outcome === 'win' ? 'YOU WON!' : 'YOU LOST';
  const amountLabel = outcome === 'win' ? 'Credited' : 'Bet Forfeited';
  const amount = outcome === 'win' ? payout : Math.floor(pot / 2);
  const resultImg = outcome === 'win' ? IMG.trophy : IMG.defeat;
  return (
    <div className="absolute inset-0 z-[190] bg-black/80 flex items-center justify-center p-5">
      <div className="relative w-full max-w-[340px] rounded-2xl bg-gradient-to-b from-[#051640] via-[#020a26] to-[#020617] border-[3px] border-cyan-500 shadow-[0_10px_40px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-4">
        <img src={resultImg} alt="" className="w-[90px] h-[90px] object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.6)]" />
        <h1 className="text-3xl font-black tracking-widest text-white">{title}</h1>
        <div className="w-full rounded-xl bg-black/40 border border-cyan-500/60 px-4 py-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-cyan-200">{amountLabel}</span>
          <span className="text-xl font-black text-yellow-300 tabular-nums">{fmt(amount)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full">
          <button
            className="carrom-primary-btn !bg-gradient-to-b !from-[#1f2937] !to-[#0f172a] !border-[#374151] !text-white"
            onClick={onHome}
          >
            Home
          </button>
          <button className="carrom-primary-btn" onClick={onNewMatch}>
            New Match
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Previous-game recovery card ─────────────────────────────────
//
// Shown on lobby entry when the user has an unacknowledged result
// for a past Carrom session. Mirrors Ludo's `RecoveryScreen` layout
// (icon + "PREVIOUS GAME" caption + outcome title + "vs Opponent"
// subtitle + amount box + secondary detail row + "Back to Lobby"
// CTA), but the detail row carries CARROM-specific stats: your
// coins sunk, opponent's coins sunk, and which side the queen
// ended up with. Localised to carrom's blue/cyan palette so it
// reads as a Carrom card, not a Ludo card with carrom data.
function CarromRecoveryScreen({
  kind, amount, platformFee, yourCoins, oppCoins, queenWith, opponents, onAck,
}: {
  kind: 'win' | 'loss' | 'kick' | 'draw';
  amount: number;
  platformFee: number;
  yourCoins: number;
  oppCoins: number;
  queenWith: 'you' | 'opp' | 'none';
  opponents: Array<{ name?: string; seat?: number; team?: string; is_bot?: boolean }>;
  onAck: () => void;
}) {
  // Outcome-driven copy + icon. Kick is treated as a loss for the
  // amount label (the bet was forfeited) but gets its own title.
  const icon = kind === 'win' ? IMG.trophy
             : kind === 'kick' ? IMG.defeat
             : kind === 'draw' ? IMG.defeat
             : IMG.defeat;
  const titleLabel = 'PREVIOUS GAME';
  const title =
    kind === 'win' ? 'YOU WON!'
    : kind === 'kick' ? 'DISCONNECTED'
    : kind === 'draw' ? 'IT WAS A DRAW'
    : 'YOU LOST';
  const amountLabel =
    kind === 'win' ? 'Credited'
    : kind === 'draw' ? 'Refunded'
    : 'Bet Forfeited';
  // Subtitle reads as a report ("Played vs Aarav") rather than a
  // live update. Falls back to outcome-flavoured copy when there
  // are no opponent profiles (older recovery rows etc.).
  const oppNames = (opponents || []).map((o) => o?.name).filter(Boolean).join(', ');
  const sub = oppNames
    ? `Played vs ${oppNames}`
    : kind === 'win'  ? 'Glory was yours'
    : kind === 'kick' ? 'You left while that match was in progress'
    : kind === 'draw' ? 'Even split — neither side broke through'
    :                   'Better luck next round';

  const queenLabel =
    queenWith === 'you' ? 'You'
    : queenWith === 'opp' ? 'Opponent'
    : '—';

  return (
    <div className="absolute inset-0 z-[200] bg-black/85 flex items-center justify-center p-5">
      <div className="relative w-full max-w-[360px] rounded-2xl bg-gradient-to-b from-[#051640] via-[#020a26] to-[#020617] border-[3px] border-cyan-500 shadow-[0_10px_40px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-3">
        <img
          src={icon}
          alt=""
          className="w-[88px] h-[88px] object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.6)]"
        />
        <div className="text-[10px] tracking-[0.3em] text-cyan-200/70 uppercase">{titleLabel}</div>
        <h1 className="text-3xl font-black tracking-widest text-white text-center leading-tight">
          {title}
        </h1>
        <p className="text-[12px] text-cyan-200 text-center max-w-[300px]">
          {sub}
        </p>

        {/* Amount box — credited (win) / refunded (draw) /
            forfeited (loss / kick). */}
        <div className="w-full rounded-xl bg-black/40 border border-cyan-500/60 px-4 py-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-cyan-200">{amountLabel}</span>
          <span className="text-xl font-black text-yellow-300 tabular-nums">{fmt(amount)}</span>
        </div>

        {/* Carrom-specific detail row — score + queen — gives the
            user a complete picture of what actually happened in
            the previous match. Hidden when both sides scored zero
            AND there was no queen, since the row would read as
            an empty placeholder. */}
        {(yourCoins > 0 || oppCoins > 0 || queenWith !== 'none') && (
          <div className="w-full rounded-xl bg-black/30 border border-cyan-500/30 px-4 py-2.5 flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-cyan-200/70 uppercase tracking-wider">Your coins</span>
              <span className="font-black text-white tabular-nums">{yourCoins}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-cyan-200/70 uppercase tracking-wider">Opponent coins</span>
              <span className="font-black text-white tabular-nums">{oppCoins}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-cyan-200/70 uppercase tracking-wider">Queen</span>
              <span className="font-black text-white">{queenLabel}</span>
            </div>
          </div>
        )}

        {kind === 'win' && platformFee > 0 && (
          <div className="text-[10px] text-cyan-200/60 tracking-wider">
            Platform fee: {fmt(platformFee)}
          </div>
        )}

        <button
          type="button"
          onClick={onAck}
          className="carrom-primary-btn w-full mt-1"
        >
          Back to Lobby
        </button>
      </div>
    </div>
  );
}

// ─── Quit confirm ────────────────────────────────────────────────────
function CarromQuitConfirm({
  amount, onStay, onQuit,
}: { amount: number; onStay: () => void; onQuit: () => void }) {
  return (
    <div className="absolute inset-0 z-[400] bg-black/75 flex items-center justify-center p-4">
      <div className="relative w-full max-w-[340px] bg-gradient-to-b from-[#051640] via-[#020a26] to-[#020617] rounded-2xl border-[3px] border-[#d4af37] shadow-[0_20px_60px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-3">
        <button
          type="button"
          aria-label="Close"
          onClick={onStay}
          className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform z-10"
        >
          ✕
        </button>
        <img src={IMG.quitWarn} className="w-16 h-16 object-contain drop-shadow" alt="" />
        <h2 className="text-2xl font-black text-white tracking-widest text-center">Forfeit the match?</h2>
        <p className="text-center text-[13px] text-cyan-200 max-w-[280px]">
          Leaving now ends the game for you and your bet of
          <span className="text-yellow-300 font-black"> {fmt(amount)} </span>
          will be forfeited.
        </p>
        <div className="grid grid-cols-2 gap-2 w-full mt-1">
          <button
            onClick={onStay}
            className="py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-cyan-500 to-cyan-700 text-white font-black tracking-widest text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition"
          >
            STAY
          </button>
          <button
            onClick={onQuit}
            className="py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#e63946] to-[#900c16] text-white font-black tracking-widest text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition"
          >
            FORFEIT
          </button>
        </div>
      </div>
    </div>
  );
}
